/**
 * AddChangeOrderModal — premium guided wizard for the **Additional Scope** module.
 *
 * Type-driven flow:
 *   - Change Order  →  4 steps: Ownership → Scope + Line Items + Linked Commitments → Production Method → Review
 *   - Upsell        →  4 steps: Ownership → Mini Calculator (line items) → (Production Method skipped) → Review
 *
 * PMs are blind to ALL pricing (unit_price, total_price, vendor cost, margin, etc).
 * Sales/Admin/Finance see the full calculator. Pricing visibility is enforced by
 * BOTH the frontend (UI hidden) AND the backend (`_scrub_pricing_for_pm`).
 */
import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../App";
import { usePmTestMode } from "../../hooks/usePmTestMode";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Upload, X, ChevronLeft, ChevronRight, ChevronDown, Search, Briefcase, Wrench,
  Eye, CheckCircle2, AlertTriangle, Lock, Paperclip, Plus, Link2, Sparkles, Wallet,
  ArrowRight, Layers, RefreshCw, TrendingUp, FileText, Clock, Mail, Copy, Send,
} from "lucide-react";
import { getProjectDisplayName, getProjectPMDisplayName } from "../../lib/projectLabel";
import { SmartUpsellPanel } from "../SmartUpsellPanel";
import { hasRecordedMoney, isFinanceRole } from "./additionalScopeAccess";

// ── Vocabulary (mirrors backend constants) ────────────────────────────────
const REQUEST_TYPES = [
  { value: "change_order", label: "Change Order", desc: "Revisions, add-ons, or production effort tied to existing work." },
  { value: "upsell",       label: "Upsell",       desc: "New scope sold to the client using TRP pricing." },
];

const CATEGORIES = [
  { value: "visualization",        label: "Visualization" },
  { value: "vexos",                label: "VEXOS" },
  { value: "tours_360",            label: "360 Tours" },
  { value: "animation",            label: "Animation" },
  { value: "marketing",            label: "Marketing" },
  { value: "collateral",           label: "Collateral" },
  { value: "website_landing_page", label: "Website / Landing Page" },
  { value: "other",                label: "Other" },
];

const SCOPE_TYPES = [
  { value: "residential",            label: "Residential" },
  { value: "amenity",                label: "Amenity" },
  { value: "exterior",               label: "Exterior" },
  { value: "interior",               label: "Interior" },
  { value: "aerial",                 label: "Aerial" },
  { value: "floorplan",              label: "Floorplan" },
  { value: "inventory_programming",  label: "Inventory Programming" },
  { value: "ui_platform",            label: "UI / Platform" },
  { value: "revision",               label: "Revision" },
  { value: "post_production",        label: "Post Production" },
  { value: "client_meeting",         label: "Client Meeting" },
  { value: "consulting",             label: "Consulting" },
  { value: "other",                  label: "Other" },
];

const ARTIST_TYPES = [
  { value: "3d_artist",            label: "3D Artist" },
  { value: "senior_3d_artist",     label: "Senior 3D Artist" },
  { value: "animator",             label: "Animator" },
  { value: "vexos_developer",      label: "VEXOS Developer" },
  { value: "designer",             label: "Designer" },
  { value: "marketing_specialist", label: "Marketing Specialist" },
  { value: "other",                label: "Other" },
];

// Simplified per July 2026 manager meeting: just Regular / Rush.
// Backend still accepts legacy "priority" on old rows.
const URGENCY = [
  { value: "standard", label: "Regular", mult: 1.0 },
  { value: "rush",     label: "Rush",    mult: 1.5 },
];

const TIMELINE_IMPACTS = [
  { value: "yes",    label: "Yes" },
  { value: "no",     label: "No" },
  { value: "unsure", label: "Unsure" },
];

const UNIT_TYPES = [
  "image", "view", "tour_point", "floor", "unit",
  "hour", "revision", "feature", "page", "custom",
];

// Scope-item categories on a change order. Saved as the label on the line's
// `category`; a label not in this list (older rows used "Camera Change",
// "Interior Design"…) comes back as a custom "Other" rather than being lost.
const CO_CHANGE_TYPES = [
  { value: "modeling",        label: "Modeling" },
  { value: "rendering",       label: "Rendering" },
  { value: "tours_360",       label: "360 Tours" },
  { value: "animation",       label: "Animation" },
  { value: "vexos",           label: "VEXOS" },
  { value: "post_production", label: "Post-Production" },
  { value: "other",           label: "Other" },
];

const PRODUCTION_METHODS = [
  { value: "in_house",   label: "In-House",   sub: "Internal team" },
  { value: "outsourced", label: "Outsourced", sub: "External vendor" },
  { value: "hybrid",     label: "Hybrid",     sub: "Both" },
];

const emptyEffort = () => ({ artistType: "3d_artist", estimatedHours: "" });
const emptyVendor = () => ({ name: "", scope: "", cost: "", currency: "USD", timeline: "" });

// Steps depend on type — UI shows the right config for the right path.
const STEPS_CHANGE_ORDER = [
  { key: "ownership", label: "Project & Scope",      icon: Briefcase },
  { key: "scope",     label: "Scope Builder",        icon: Layers },
  { key: "method",    label: "Production & Timeline",icon: Wrench },
  { key: "review",    label: "Review",               icon: CheckCircle2 },
  { key: "send",      label: "Send to Client",       icon: Mail },
];
const STEPS_UPSELL = [
  { key: "ownership", label: "Project & Scope",   icon: Briefcase },
  { key: "calculator",label: "Scope Builder",     icon: Sparkles },
  { key: "method",    label: "Timeline & Notes",  icon: Wallet },
  { key: "review",    label: "Review & Routing",  icon: CheckCircle2 },
];

// Frontend-side operational descriptors for catalog items — gives the cards
// editorial weight without bloating the backend payload.
const CATALOG_DESCRIPTIONS = {
  viz_exterior_render:        "Photoreal exterior architectural visualization.",
  viz_interior_render:        "Photoreal interior visualization with lifestyle styling.",
  viz_aerial_render:          "High-altitude aerial visualization for context and siting.",
  viz_conceptual_render:      "Early-stage atmospheric visualization for fundraising or pre-design.",
  viz_revision_pack:          "Curated revision pack for an approved rendering deliverable.",
  viz_3d_floorplan:           "Stylized 3D floorplan illustration.",
  viz_floorplan_redraw:       "Updated 2D floorplan redraw with revised partitions.",
  tour_residential:           "Immersive 360° walkthrough across a residence.",
  tour_amenity:               "360° tour anchored on amenity spaces.",
  tour_additional_point:      "Additional 360° capture point in an existing tour.",
  tour_revision_pack:         "Revision pack for an existing 360 tour deliverable.",
  anim_30s:                   "Short cinematic animation (~30 seconds).",
  anim_60s:                   "Mid-length cinematic animation (~60 seconds).",
  anim_90s:                   "Hero cinematic animation (~90 seconds).",
  anim_revision:              "Animation revision pack — minor cuts and re-grades.",
  anim_ai_lifestyle:          "AI-assisted lifestyle film for social and digital.",
  vexos_orbit:                "Additional cinematic VEXOS navigation orbit.",
  vexos_day_night:            "Day/night toggle layer for VEXOS environment.",
  vexos_floorplate:           "Additional residence floorplate for the VEXOS layer.",
  vexos_inventory:            "Inventory programming + availability layer.",
  vexos_residence_experience: "Detailed residence-level VEXOS interactive experience.",
  vexos_amenity_experience:   "Amenity-focused VEXOS interactive experience.",
  vexos_neighborhood:         "Neighborhood explorer integration for VEXOS.",
  vexos_custom_ui:            "Custom UI feature embedded into the VEXOS experience.",
  vexos_extended_license:     "Extended license window for the VEXOS deliverable.",
  mkt_pitch_deck:             "Premium investor / sales pitch deck.",
  mkt_brochure:               "Print-ready or digital brochure.",
  mkt_one_pager:              "Single-page collateral asset.",
  mkt_social_pack:            "Social-ready creative pack (multi-format).",
  mkt_landing_page:           "Standalone landing page build.",
  mkt_email_campaign:         "Email campaign creative + setup.",
  mkt_meta_setup:             "Meta (Facebook/Instagram) campaign setup.",
  mkt_monthly_management:     "Monthly campaign management retainer.",
};

const formatCurrency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Sales Rep routing whitelist — Step 1 dropdown is hard-restricted to these emails
// because it routes Additional Scope to Sales only. Adjust here when a new rep joins.
const ALLOWED_SALES_REP_EMAILS = [
  "jordan@therenderpros.com",
  "julian@therenderpros.com",
];

export function AddChangeOrderModal({ open, onOpenChange, projectId, projectName, onSaved, forcedType, editingCo }) {
  const [step, setStep] = useState(0);
  // Editing reuses the whole wizard: same fields, same validation, same
  // pricing preview — only the verb and the endpoint change.
  const isEditing = !!editingCo?.id;
  // A row with money already collected needs finance to say out loud that the
  // re-price is intentional (the backend 409s otherwise).
  const [repriceConfirmed, setRepriceConfirmed] = useState(false);

  // Step 1 — ownership + TYPE
  const [programs, setPrograms] = useState([]);
  const [projectQuery, setProjectQuery] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || "");
  const [selectedProject, setSelectedProject] = useState(null);
  const [title, setTitle] = useState("");
  const [requestType, setRequestType] = useState(forcedType || "change_order");
  const [clientEmail, setClientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [emailSource, setEmailSource] = useState(null); // null | "project" | "intake" | "manual"
  const [emailEditing, setEmailEditing] = useState(false);
  const [salesRepId, setSalesRepId] = useState("");
  const [salesRepName, setSalesRepName] = useState("");
  const [salesRepEmail, setSalesRepEmail] = useState("");

  // Step 2 — scope or calculator (shared structured line items)
  const [category, setCategory] = useState("visualization");
  const [categoryCustom, setCategoryCustom] = useState("");
  const [scopeType, setScopeType] = useState("residential");
  const [description, setDescription] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [lineItems, setLineItems] = useState([]); // {id, category, item_name, ...}
  // New change orders are itemized: every scope item carries its own
  // production estimate and price, and the client can approve items one by
  // one. Rows raised before that keep their single order-level estimate.
  const [itemized, setItemized] = useState(true);
  const isItemized = requestType === "change_order" && itemized;
  const [linkedCommitmentIds, setLinkedCommitmentIds] = useState([]);

  // Step 3 — production method (Change Order) or timeline-only (Upsell)
  const [productionMethod, setProductionMethod] = useState("in_house");
  const [inHouseEfforts, setInHouseEfforts] = useState([{ artistType: "3d_artist", estimatedHours: "" }]);
  const [urgency, setUrgency] = useState("standard");
  const [desiredCompletionDate, setDesiredCompletionDate] = useState("");
  const [timelineImpact, setTimelineImpact] = useState("unsure");
  const [outsourceManagerContacted, setOutsourceManagerContacted] = useState(true);
  const [vendors, setVendors] = useState([{ name: "", scope: "", cost: "", currency: "USD", timeline: "" }]);

  // Custom client price. The engine's recommendation never disappears — it stays
  // on screen as the reference the typed figure is being judged against, and
  // "Use recommended" puts the order back on it.
  const [priceOverrideOn, setPriceOverrideOn] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");

  const updateVendor = (idx, field, val) =>
    setVendors(v => v.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  const addVendor = () => setVendors(v => [...v, { name: "", scope: "", cost: "", currency: "USD", timeline: "" }]);
  const removeVendor = (idx) => setVendors(v => v.filter((_, i) => i !== idx));

  const updateEffort = (idx, field, val) =>
    setInHouseEfforts(v => v.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  const addEffort = () => setInHouseEfforts(v => [...v, { artistType: "3d_artist", estimatedHours: "" }]);
  const removeEffort = (idx) => setInHouseEfforts(v => v.filter((_, i) => i !== idx));

  // Total in-house hours across all efforts (for pricing preview)
  const totalInHouseHours = inHouseEfforts.reduce((s, e) => s + (parseFloat(e.estimatedHours) || 0), 0);

  // Total vendor cost across all studios (for pricing preview)
  const totalVendorCost = vendors.reduce((s, v) => s + (parseFloat(v.cost) || 0), 0);
  // Markup is tiered and non-editable — computed from vendor cost server-side.
  const getOutsourceMultiplier = (cost) => {
    const c = parseFloat(cost) || 0;
    if (c <= 1000) return 2.00;
    if (c <= 3000) return 1.75;
    if (c <= 5000) return 1.50;
    return 1.30;
  };

  // Settings + people + project commitments
  const [billableRates, setBillableRates] = useState(null);
  const [salespeople, setSalespeople] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // used to resolve PM names by id when not joined
  const [me, setMe] = useState(null);
  const [upsellCatalog, setUpsellCatalog] = useState({});
  const [projectCommitments, setProjectCommitments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [smartUpsellOpen, setSmartUpsellOpen] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  // Step 5 — email preview (change orders only)
  const [createdCo, setCreatedCo] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Treat PM Test Mode as a PM so admins previewing PM mode see exactly what a
  // PM sees (no pricing) — and as belt-and-suspenders for financial hiding.
  const pmTestMode = usePmTestMode();
  const isPmRole = useMemo(
    () => pmTestMode.isActive || me?.role === "Project Manager" || me?.role === "Program Manager" || me?.role === "Marketing PM",
    [me, pmTestMode.isActive]
  );
  const isFinance = isFinanceRole(me?.role) && !pmTestMode.isActive;
  // Every edit from this wizard resubmits the scope, so any edit of a row with
  // a payment against it is a re-price as far as the backend is concerned.
  const needsRepriceConfirm = isEditing && hasRecordedMoney(editingCo);
  // Editing skips the send step — the client link already exists and reads live.
  const STEPS = (requestType === "upsell" ? STEPS_UPSELL : STEPS_CHANGE_ORDER)
    .filter(st => !(isEditing && st.key === "send"));

  // ── Init / hydrate ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setRepriceConfirmed(false);
    if (editingCo?.id) hydrateFromCo(editingCo);
    else setSelectedProjectId(projectId || "");

    apiClient.get("/programs").then(res => {
      setPrograms(res.data || []);
      if (projectId) {
        const p = (res.data || []).find(x => x.id === projectId || x._id === projectId);
        if (p) setSelectedProject(p);
      }
    }).catch(() => setPrograms([]));

    apiClient.get("/change-orders/settings/billable-rates")
      .then(res => setBillableRates(res.data))
      .catch(() => setBillableRates(null));

    apiClient.get("/change-orders/settings/upsell-catalog")
      .then(res => setUpsellCatalog(res.data?.catalog || {}))
      .catch(() => setUpsellCatalog({}));

    apiClient.get("/auth/me").then(res => setMe(res.data)).catch(() => setMe(null));

    // Sales Rep dropdown is HARD-restricted to the routing whitelist (see
    // ALLOWED_SALES_REP_EMAILS). Pull the full users list and filter — never
    // show PMs, admins, finance, artists, or test accounts here.
    apiClient.get("/users").then(res => {
      const all = res.data || [];
      setAllUsers(all);
      const filtered = all.filter(u =>
        ALLOWED_SALES_REP_EMAILS.includes((u.email || "").toLowerCase())
      );
      setSalespeople(filtered);
    }).catch(() => { setAllUsers([]); setSalespeople([]); });
  }, [open, projectId, editingCo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill PM/sales when project is selected + load commitments + look up client email
  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedProject(null); setProjectCommitments([]);
      setClientEmail(""); setEmailSource(null); setEmailEditing(false);
      return;
    }
    const p = programs.find(x => x.id === selectedProjectId || x._id === selectedProjectId);
    if (p) {
      setSelectedProject(p);
      // Editing already carries its own rep + client email. Re-deriving them
      // from the project would silently overwrite whatever was actually sent.
      if (isEditing) {
        apiClient.get(`/deliverables?program_id=${selectedProjectId}`)
          .then(res => setProjectCommitments(res.data || []))
          .catch(() => setProjectCommitments([]));
        return;
      }
      const projectRepEmail = (p.sales_rep_email || "").toLowerCase();
      const inWhitelist = ALLOWED_SALES_REP_EMAILS.includes(projectRepEmail);
      if (inWhitelist && !salesRepId) {
        setSalesRepId(p.sales_rep_id);
        setSalesRepName(p.sales_rep_name || "");
        setSalesRepEmail(p.sales_rep_email || "");
      }
      // Auto-fill client email: use program field first, fall back to intake lookup
      if (p.client_email) {
        setClientEmail(p.client_email);
        setEmailSource("project");
        setEmailEditing(false);
      } else {
        setClientEmail("");
        setEmailSource(null);
        setEmailEditing(false);
        apiClient.get(`/intake/submissions?program_id=${selectedProjectId}`)
          .then(res => {
            const sub = (res.data || [])[0];
            if (sub?.client_email) {
              setClientEmail(sub.client_email);
              setEmailSource("intake");
              setEmailEditing(false);
            }
          })
          .catch(() => {});
      }
    }
    apiClient.get(`/deliverables?program_id=${selectedProjectId}`)
      .then(res => setProjectCommitments(res.data || []))
      .catch(() => setProjectCommitments([]));
  }, [selectedProjectId, programs, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load an existing row back into the wizard ────────────────────────────
  // Inverse of `buildPayload`: the stored doc is flat and snake_cased, the
  // wizard's state is nested and camelCased, so each side has to be unpacked.
  const hydrateFromCo = (co) => {
    setSelectedProjectId(co.project_id || "");
    setTitle(co.title || "");
    setRequestType(co.request_type || "change_order");
    setClientEmail(co.client_email || "");
    setEmailSource(co.client_email ? "manual" : null);
    setEmailEditing(false);
    setCcEmail("");
    setSalesRepId(co.sales_rep_id || "");
    setSalesRepName(co.sales_rep_name || "");
    setSalesRepEmail(co.sales_rep_email || "");

    setCategory(co.category || "visualization");
    setCategoryCustom(co.category_custom || "");
    setScopeType(co.scope_type || "residential");
    setDescription(co.description || "");
    setInternalNotes(co.internal_notes || "");
    setAttachments(co.attachments || []);
    setLinkedCommitmentIds(co.linked_commitment_ids || []);
    // Change-order lines flatten their change-type into `category` on save,
    // so map the label back onto the picker; anything unrecognised becomes a
    // custom "Other" rather than being dropped.
    setItemized(!!co.itemized);
    setLineItems((co.line_items || []).map(li => {
      const matched = CO_CHANGE_TYPES.find(t => t.label === li.category);
      return {
        ...li,
        quantity: Number(li.quantity ?? 1),
        unit_price: Number(li.unit_price ?? 0),
        total_price: Number(li.total_price ?? 0),
        co_type: matched ? matched.value : "other",
        co_type_custom: matched || !li.category ? "" : li.category,
        production_method: li.production_method || "in_house",
        item_efforts: li.in_house_efforts?.length
          ? li.in_house_efforts.map(e => ({
              artistType: e.artist_type || "3d_artist",
              estimatedHours: e.estimated_hours == null ? "" : String(e.estimated_hours),
            }))
          : [emptyEffort()],
        item_vendors: li.vendors?.length
          ? li.vendors.map(v => ({
              name: v.name || "",
              scope: v.scope || "",
              cost: v.cost == null ? "" : String(v.cost),
              currency: v.currency || "USD",
              timeline: v.timeline || "",
            }))
          : [emptyVendor()],
        // A price somebody typed comes back typed; otherwise the item keeps
        // billing its recommendation.
        price_override: li.price_manually_set && li.total_price != null ? String(li.total_price) : "",
      };
    }));

    setProductionMethod(co.production_method || "in_house");
    setInHouseEfforts(
      co.in_house_efforts?.length
        ? co.in_house_efforts.map(e => ({
            artistType: e.artist_type || "3d_artist",
            estimatedHours: e.estimated_hours == null ? "" : String(e.estimated_hours),
          }))
        : [{
            artistType: co.artist_type || "3d_artist",
            estimatedHours: co.estimated_hours == null ? "" : String(co.estimated_hours),
          }]
    );
    setUrgency(co.urgency || "standard");
    setDesiredCompletionDate(co.desired_completion_date || "");
    setTimelineImpact(co.timeline_impact || "unsure");
    setOutsourceManagerContacted(co.outsource_manager_contacted !== false);
    setVendors(
      co.vendors?.length
        ? co.vendors.map(v => ({
            name: v.name || "",
            scope: v.scope || "",
            cost: v.cost == null ? "" : String(v.cost),
            currency: v.currency || "USD",
            timeline: v.timeline || "",
          }))
        : [{
            name: co.vendor_name || "",
            scope: "",
            cost: co.vendor_cost == null ? "" : String(co.vendor_cost),
            currency: co.vendor_currency || "USD",
            timeline: co.vendor_timeline || "",
          }]
    );
    // A price somebody typed comes back as a typed price, not as a fresh
    // recommendation — otherwise saving an edit would re-price the order.
    const storedPrice = co.final_client_price ?? co.price;
    const wasManual = co.price_manually_set != null
      ? !!co.price_manually_set
      : storedPrice != null && Math.abs(Number(storedPrice) - Number(co.recommended_price || 0)) > 0.01;
    setPriceOverrideOn(!!wasManual && storedPrice != null);
    setPriceOverride(wasManual && storedPrice != null ? String(storedPrice) : "");

    setCreatedCo(null); setEmailSubject(""); setEmailBody("");
    setEmailSending(false); setEmailSent(false);
  };

  const reset = () => {
    setStep(0);
    setTitle(""); setRequestType(forcedType || "change_order");
    setCategory("visualization"); setCategoryCustom("");
    setScopeType("residential"); setDescription(""); setInternalNotes("");
    setAttachments([]); setLineItems([]); setLinkedCommitmentIds([]);
    setItemized(true);
    setProductionMethod("in_house");
    setInHouseEfforts([{ artistType: "3d_artist", estimatedHours: "" }]); setUrgency("standard");
    setDesiredCompletionDate(""); setTimelineImpact("unsure");
    setOutsourceManagerContacted(true);
    setVendors([{ name: "", scope: "", cost: "", currency: "USD", timeline: "" }]);
    setPriceOverrideOn(false); setPriceOverride("");
    setCreatedCo(null); setEmailSubject(""); setEmailBody("");
    setEmailSending(false); setEmailSent(false);
    setCcEmail(""); setEmailSource(null); setEmailEditing(false);
  };

  // ── Pricing computation ─────────────────────────────────────────────────
  // Upsell: sum of line items.
  // Change Order: operational pricing (in-house hours × rate × urgency + outsource markup) + line items add-ons.
  const lineItemsTotal = useMemo(
    () => lineItems.reduce((s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)), 0),
    [lineItems]
  );

  const operationalPrice = useMemo(() => {
    if (requestType !== "change_order") return 0;
    const urg = URGENCY.find(u => u.value === urgency)?.mult || 1;
    let total = 0;
    if (productionMethod === "in_house" || productionMethod === "hybrid") {
      total += inHouseEfforts.reduce((s, e) => {
        const rate = billableRates?.rates?.[e.artistType] ?? 85;
        const hrs = parseFloat(e.estimatedHours) || 0;
        return s + hrs * rate * urg;
      }, 0);
    }
    if (productionMethod === "outsourced" || productionMethod === "hybrid") {
      // Rush applies to outsourced work too (mirrors backend _compute_recommended_price)
      total += totalVendorCost * getOutsourceMultiplier(totalVendorCost) * urg;
    }
    return Math.round(total * 100) / 100;
  }, [requestType, productionMethod, urgency, inHouseEfforts, totalVendorCost, billableRates]);

  // ── Itemized change orders: each scope item prices itself ──────────────
  // Mirrors backend _price_scope_items: hours × rate × urgency, plus the
  // item's own studio cost × its tiered multiplier × urgency. A price typed on
  // the item wins; a blank box bills the recommendation.
  const urgencyMult = URGENCY.find(u => u.value === urgency)?.mult || 1;
  const itemRecommended = (li) => {
    const method = li.production_method;
    let total = 0;
    if (method === "in_house" || method === "hybrid") {
      total += (li.item_efforts || []).reduce((s, e) => {
        const rate = billableRates?.rates?.[e.artistType] ?? 85;
        return s + (parseFloat(e.estimatedHours) || 0) * rate * urgencyMult;
      }, 0);
    }
    if (method === "outsourced" || method === "hybrid") {
      const cost = (li.item_vendors || []).reduce((s, v) => s + (parseFloat(v.cost) || 0), 0);
      total += cost * getOutsourceMultiplier(cost) * urgencyMult;
    }
    return Math.round(total * 100) / 100;
  };
  const itemOverride = (li) => {
    if (isPmRole) return null;
    const raw = String(li.price_override ?? "").trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
  };
  const itemPrice = (li) => itemOverride(li) ?? itemRecommended(li);
  const itemVendorCost = (li) => (li.production_method === "outsourced" || li.production_method === "hybrid")
    ? (li.item_vendors || []).reduce((s, v) => s + (parseFloat(v.cost) || 0), 0)
    : 0;
  // A declined item stays on the order but is no longer billed.
  const billedItems = lineItems.filter(li => li.client_decision !== "declined");
  const scopeTotals = {
    billed: Math.round(billedItems.reduce((s, li) => s + itemPrice(li), 0) * 100) / 100,
    proposed: Math.round(lineItems.reduce((s, li) => s + itemPrice(li), 0) * 100) / 100,
    recommended: Math.round(billedItems.reduce((s, li) => s + itemRecommended(li), 0) * 100) / 100,
    vendorCost: billedItems.reduce((s, li) => s + itemVendorCost(li), 0),
  };

  const computedRecommendedPrice = useMemo(() => {
    if (isPmRole) return null;
    if (isItemized) return scopeTotals.recommended;
    if (requestType === "upsell") return Math.round(lineItemsTotal * 100) / 100;
    return Math.round((operationalPrice + lineItemsTotal) * 100) / 100;
  }, [isPmRole, isItemized, scopeTotals.recommended, requestType, operationalPrice, lineItemsTotal]);

  // A blank or non-numeric box is not an override — the recommendation still
  // stands, so a half-typed figure can never be the price that gets billed.
  const overrideAmount = useMemo(() => {
    if (isPmRole || !priceOverrideOn) return null;
    const raw = String(priceOverride).trim();
    if (raw === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100) / 100;
  }, [isPmRole, priceOverrideOn, priceOverride]);

  // Itemized orders have no order-level override — the total is always the
  // sum of the items, so the client's per-item choices add up.
  const isOverridden = !isItemized && overrideAmount !== null;
  // The one figure every money surface downstream reads: what the client is billed.
  const effectivePrice = isPmRole ? null
    : isItemized ? scopeTotals.billed
    : isOverridden ? overrideAmount : computedRecommendedPrice;

  const marginEstimate = useMemo(() => {
    if (isPmRole) return null;
    if (requestType !== "change_order") return null;
    if (isItemized) {
      return scopeTotals.vendorCost > 0 ? Math.round((effectivePrice - scopeTotals.vendorCost) * 100) / 100 : null;
    }
    if (productionMethod === "outsourced" || productionMethod === "hybrid") {
      return effectivePrice ? Math.round((effectivePrice - totalVendorCost) * 100) / 100 : null;
    }
    return null;
  }, [isPmRole, requestType, isItemized, scopeTotals.vendorCost, productionMethod, totalVendorCost, effectivePrice]);

  // ── AI scope-summary enhancement ────────────────────────────────────────
  const handleEnhanceDescription = async () => {
    if (!description.trim()) { toast.error("Type a short description first"); return; }
    setEnhancing(true);
    try {
      const res = await apiClient.post("/change-orders/ai/enhance-description", {
        text: description,
        request_type: requestType,
        project_name: selectedProject ? getProjectDisplayName(selectedProject) : null,
        category: CATEGORIES.find(c => c.value === category)?.label || category,
      });
      if (res.data?.enhanced) {
        setDescription(res.data.enhanced);
        toast.success("Summary formalized — review before continuing");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI enhancement unavailable";
      toast.error(typeof msg === "string" ? msg : "AI enhancement unavailable");
    } finally {
      setEnhancing(false);
    }
  };

  const EnhanceButton = () => (
    <button
      type="button"
      onClick={handleEnhanceDescription}
      disabled={enhancing || !description.trim()}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:text-violet-800 disabled:opacity-40 disabled:cursor-default transition-colors"
      data-testid="co-enhance-description"
      title="Rewrite your notes into formal client-facing copy"
    >
      <Sparkles className={`w-3 h-3 ${enhancing ? "animate-pulse" : ""}`} />
      {enhancing ? "Enhancing…" : "Enhance with AI"}
    </button>
  );

  // ── Attachments ─────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    const read = await Promise.all(files.map(f => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        filename: f.name, content_type: f.type, size_bytes: f.size,
        data_base64: (reader.result || "").toString().split(",")[1] || ""
      });
      reader.onerror = reject;
      reader.readAsDataURL(f);
    })));
    setAttachments(prev => [...prev, ...read]);
    e.target.value = "";
  };

  // ── Line items helpers ──────────────────────────────────────────────────
  const addCatalogItem = (category, item) => {
    setLineItems(prev => [...prev, {
      id: crypto.randomUUID(),
      category,
      item_name: item.label,
      pricing_key: item.key,
      quantity: 1,
      unit_type: item.unit_type,
      unit_price: item.default_unit_price ?? 0,
      total_price: item.default_unit_price ?? 0,
      description: "",
      related_space: "",
      linked_commitment_id: null,
    }]);
  };
  const addCustomLineItem = () => {
    setLineItems(prev => [...prev, {
      id: crypto.randomUUID(),
      category: "Custom",
      co_type: "modeling",
      co_type_custom: "",
      item_name: "",
      pricing_key: null,
      quantity: 1,
      unit_type: "feature",
      unit_price: 0,
      total_price: 0,
      description: "",
      related_space: "",
      linked_commitment_id: null,
      // Change-order items only: the item's own production estimate (Step 3)
      // and an optional typed price ("" = bill the recommendation).
      production_method: "in_house",
      item_efforts: [emptyEffort()],
      item_vendors: [emptyVendor()],
      price_override: "",
    }]);
  };
  const updateLineItem = (id, patch) => {
    setLineItems(prev => prev.map(li => {
      if (li.id !== id) return li;
      const next = { ...li, ...patch };
      next.total_price = Math.round(Number(next.quantity || 0) * Number(next.unit_price || 0) * 100) / 100;
      return next;
    }));
  };
  const removeLineItem = (id) => setLineItems(prev => prev.filter(li => li.id !== id));

  // ── Step gating ─────────────────────────────────────────────────────────
  const filteredPrograms = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    // Mirror ProgramsView "Active" tab: exclude Delivered and Closed
    const activeOnly = programs.filter(p => {
      const stage = p.stage || p.status || "";
      return stage !== "Delivered" && stage !== "Closed";
    });
    const filtered = !q
      ? activeOnly
      : activeOnly.filter(p =>
          getProjectDisplayName(p).toLowerCase().includes(q) ||
          (p.client_name || "").toLowerCase().includes(q) ||
          (p.address || "").toLowerCase().includes(q)
        );
    // Sort alphabetically (A→Z) by the SAME computed display name used in the UI.
    // Case + whitespace insensitive; localeCompare for natural number ordering
    // ("7300 Belle Meade" sorts before "Mike Lefavor"; "123 Ocean Avenue" first).
    const sorted = [...filtered].sort((a, b) => {
      const la = getProjectDisplayName(a).trim().toLowerCase();
      const lb = getProjectDisplayName(b).trim().toLowerCase();
      return la.localeCompare(lb, undefined, { numeric: true, sensitivity: "base" });
    });
    return sorted.slice(0, 50);
  }, [programs, projectQuery]);

  const canAdvanceStep1 = !!selectedProjectId && (requestType === "change_order" || !!title.trim());
  const canAdvanceStep2 = (() => {
    if (requestType === "upsell") return lineItems.length > 0 && lineItems.every(li => li.item_name.trim() && Number(li.quantity) > 0);
    if (isItemized) return lineItems.length > 0 && lineItems.every(li => (li.item_name || "").trim());
    return !!description.trim();
  })();
  // An item is ready once it has a production estimate — or, for someone who
  // can see prices, a typed price. PMs never see studio costs, so a cost is
  // never required from them (see the step-3 trap in the vendor check below).
  const itemProductionReady = (li) => {
    if (itemOverride(li) !== null) return true;
    const efforts = li.item_efforts || [];
    const vendorsList = li.item_vendors || [];
    const effortsReady = efforts.length > 0 && efforts.every(e => e.estimatedHours && e.artistType);
    const vendorsReady = vendorsList.length > 0 && vendorsList.every(v => v.name && (isPmRole || v.cost));
    if (li.production_method === "in_house") return effortsReady;
    if (li.production_method === "outsourced") return vendorsReady;
    if (li.production_method === "hybrid") return effortsReady && vendorsReady;
    return false;
  };
  const canAdvanceStep3 = (() => {
    if (requestType === "upsell") return true; // step 3 is just timeline/notes for upsell
    if (isItemized) return lineItems.length > 0 && lineItems.every(itemProductionReady);
    // PMs never see the vendor cost field ("Finalized by Sales."), so requiring a
    // cost from them would leave them permanently stuck on this step.
    const vendorsReady = vendors.length > 0 && vendors.every(v => v.name && (isPmRole || v.cost));
    const effortsReady = inHouseEfforts.length > 0 && inHouseEfforts.every(e => e.estimatedHours && e.artistType);
    if (productionMethod === "in_house") return effortsReady;
    if (productionMethod === "outsourced") return vendorsReady;
    if (productionMethod === "hybrid") return effortsReady && vendorsReady;
    return false;
  })();

  // ── Submit ──────────────────────────────────────────────────────────────
  const orderLevelProduction = requestType === "change_order" && !isItemized;
  const anyOutsourcing = isItemized
    ? lineItems.some(li => li.production_method === "outsourced" || li.production_method === "hybrid")
    : orderLevelProduction && ["outsourced", "hybrid"].includes(productionMethod);
  const buildPayload = () => {
    const categoryLabel = CATEGORIES.find(c => c.value === category)?.label || category;
    const autoTitle = requestType === "change_order"
      ? `${categoryLabel} Change Order`
      : title.trim();
    return {
      project_id: selectedProjectId,
      title: autoTitle,
      request_type: requestType,
      category, category_custom: category === "other" ? categoryCustom : null,
      scope_type: scopeType,
      description: description.trim() || (requestType === "upsell" ? `${lineItems.length} item(s)` : ""),
      internal_notes: internalNotes.trim() || null,
      attachments,
      line_items: lineItems.map(li => {
        const coTypeLabel = li.co_type === "other"
          ? (li.co_type_custom || "Other")
          : (CO_CHANGE_TYPES.find(t => t.value === li.co_type)?.label || li.category || "Other");
        if (isItemized) {
          const method = li.production_method;
          // The backend re-prices every item itself; the figures sent here are
          // what Sales typed, and a PM's zeros are ignored there.
          return {
            id: li.id,
            category: coTypeLabel,
            item_name: li.item_name.trim(),
            description: (li.description || "").trim() || null,
            quantity: Number(li.quantity || 1),
            unit_type: li.unit_type || null,
            related_space: li.related_space || null,
            linked_commitment_id: li.linked_commitment_id || null,
            pricing_key: null,
            production_method: method,
            in_house_efforts: method === "in_house" || method === "hybrid"
              ? (li.item_efforts || []).map(e => ({ artist_type: e.artistType, estimated_hours: parseFloat(e.estimatedHours) || 0 }))
              : [],
            vendors: method === "outsourced" || method === "hybrid"
              ? (li.item_vendors || []).map(v => ({
                  name: v.name, scope: v.scope || "", cost: isPmRole ? 0 : (parseFloat(v.cost) || 0),
                  currency: v.currency || "USD", timeline: v.timeline || "",
                }))
              : [],
            unit_price: 0,
            total_price: isPmRole ? 0 : itemPrice(li),
            price_manually_set: isPmRole ? null : itemOverride(li) !== null,
          };
        }
        return {
          id: li.id,
          category: requestType === "change_order" ? coTypeLabel : li.category,
          item_name: li.item_name,
          // These three have no field in the change-order UI. On create they
          // are empty either way; on an edit, blanking them would throw away
          // what the row already carried.
          description: requestType === "change_order" ? (li.description || null) : li.description,
          quantity: Number(li.quantity || 1),
          unit_type: li.unit_type || null,
          related_space: li.related_space || null,
          linked_commitment_id: li.linked_commitment_id || null,
          pricing_key: li.pricing_key || null,
          unit_price: isPmRole ? 0 : Number(li.unit_price || 0),
          total_price: isPmRole ? 0 : Number(li.total_price || 0),
        };
      }),
      linked_commitment_ids: linkedCommitmentIds,
      itemized: isItemized,
      // Itemized orders carry their production on the items; the backend
      // derives the order-level mirrors from them.
      production_method: orderLevelProduction ? productionMethod : null,
      estimated_hours: orderLevelProduction && ["in_house", "hybrid"].includes(productionMethod) ? (parseFloat(inHouseEfforts[0]?.estimatedHours) || 0) : null,
      artist_type: orderLevelProduction && ["in_house", "hybrid"].includes(productionMethod) ? (inHouseEfforts[0]?.artistType || null) : null,
      in_house_efforts: orderLevelProduction && ["in_house", "hybrid"].includes(productionMethod)
        ? inHouseEfforts.map(e => ({ artist_type: e.artistType, estimated_hours: parseFloat(e.estimatedHours) || 0 }))
        : null,
      urgency,
      desired_completion_date: desiredCompletionDate || null,
      timeline_impact: timelineImpact,
      outsource_manager_contacted: anyOutsourcing ? outsourceManagerContacted : null,
      vendor_name: orderLevelProduction && ["outsourced", "hybrid"].includes(productionMethod) ? (vendors[0]?.name || null) : null,
      vendor_cost: orderLevelProduction && ["outsourced", "hybrid"].includes(productionMethod) ? (parseFloat(vendors[0]?.cost) || 0) : null,
      vendor_currency: vendors[0]?.currency || "USD",
      vendor_timeline: vendors[0]?.timeline || null,
      vendors: orderLevelProduction && ["outsourced", "hybrid"].includes(productionMethod)
        ? vendors.map(s => ({ name: s.name, scope: s.scope || "", cost: parseFloat(s.cost) || 0, currency: s.currency, timeline: s.timeline }))
        : null,
      markup_percent: null,
      client_email: clientEmail.trim() || null,
      cc_emails: ccEmail.trim() ? [ccEmail.trim()] : [],
      sales_rep_id: salesRepId || null,
      sales_rep_name: salesRepName || null,
      sales_rep_email: salesRepEmail || null,
      // 0 means "use the engine's recommendation" — the backend prices it.
      price: isOverridden ? overrideAmount : 0,
      price_manually_set: isPmRole || isItemized ? null : isOverridden,
      quantity: 1,
      client_approved: false,
      approval_method: "not_yet_presented",
    };
  };

  // An edit sends the same shape minus the fields that are fixed for the life
  // of the row: which project it belongs to, whether it is a change order or an
  // upsell (they price and bill differently), and the approval state, which
  // moves only through its own endpoints.
  const buildEditPayload = () => {
    const {
      project_id, request_type, client_approved, approval_method,
      price, quantity, cc_emails, ...editable
    } = buildPayload();
    // A typed price is sent; a `price: 0` is not. The backend writes what it is
    // given, so sending the placeholder zero on a description-only edit would
    // wipe the order's value. Clearing an override is the flag's job, not 0's.
    if (isOverridden) editable.price = price;
    return editable;
  };

  const handleSubmit = async () => {
    // Saving an edit can fire from any step, so point at the one that is short
    // rather than just refusing — otherwise the button looks broken.
    const incomplete = !canAdvanceStep1 ? 0 : !canAdvanceStep2 ? 1 : !canAdvanceStep3 ? 2 : null;
    if (incomplete !== null) {
      setStep(incomplete);
      toast.error(`Complete "${STEPS[incomplete].label}" first`);
      return;
    }
    if (isEditing) return handleSaveEdit();
    setSaving(true);
    try {
      const res = await apiClient.post("/change-orders", buildPayload());
      const co = res.data;

      if (requestType === "change_order") {
        setCreatedCo(co);
        const approvalUrl = co.public_token
          ? `${window.location.origin}/approval/${co.public_token}`
          : null;
        const projectName = getProjectDisplayName(selectedProject);
        const categoryLabel = CATEGORIES.find(c => c.value === category)?.label || category;
        setEmailSubject(`Your Change Order is Ready — ${projectName}`);
        setEmailBody(isItemized
          ? `Hi there,\n\nYour Change Order for ${projectName} is ready for your review.\n\nWe've broken the work down item by item, with the price of each. You can approve the whole change order, or only the items you'd like us to go ahead with — the total updates as you choose.\n\nReview and approve here:\n\n${approvalUrl || "[approval link will appear here]"}\n\nPlease note: this approval & payment link expires in 7 days.\n\nOnce payment is received, production on the approved items begins immediately.\n\nIf you have any questions, please reply to this email or reach out to your project manager.\n\nThank you,\nThe Render Pros Team`
          : `Hi there,\n\nYour ${categoryLabel} Change Order for ${projectName} is ready for your review and approval.\n\nPlease click the link below to view the scope, approve, and complete payment:\n\n${approvalUrl || "[approval link will appear here]"}\n\nPlease note: this approval & payment link expires in 7 days.\n\nOnce payment is received, production will begin immediately.\n\nIf you have any questions, please reply to this email or reach out to your project manager.\n\nThank you,\nThe Render Pros Team`
        );
        setStep(4);
        toast.success("Change order created — preview the email below");
      } else {
        toast.success("Upsell submitted successfully");
        reset();
        onSaved?.();
        onOpenChange(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to create additional scope";
      toast.error(typeof msg === "string" ? msg : "Failed to create additional scope");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const qs = needsRepriceConfirm && repriceConfirmed ? "?force=true" : "";
      const res = await apiClient.patch(`/change-orders/${editingCo.id}${qs}`, buildEditPayload());
      toast.success(`${requestType === "upsell" ? "Upsell" : "Change order"} updated`);
      onSaved?.(res.data);
      onOpenChange(false);
      reset();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to save changes";
      toast.error(typeof msg === "string" ? msg : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!createdCo?.id) return;
    setEmailSending(true);
    try {
      await apiClient.post(`/change-orders/${createdCo.id}/send-to-client`, {
        custom_message: emailBody,
        custom_subject: emailSubject,
        cc_emails: ccEmail.trim() ? [ccEmail.trim()] : [],
      });
      setEmailSent(true);
      toast.success("Email sent to client");
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send email";
      toast.error(typeof msg === "string" ? msg : "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  const handleCopyApprovalLink = () => {
    if (!createdCo?.public_token) return;
    const url = `${window.location.origin}/approval/${createdCo.public_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Approval link copied"));
  };

  // Premium thin stepper with connector lines
  const StepHeader = () => (
    <div className="flex items-center px-7 pt-4 pb-3" data-testid="co-wizard-stepper">
      {STEPS.map((s, idx) => {
        const Icon = s.icon;
        const active = step === idx;
        const done = step > idx;
        const isLast = idx === STEPS.length - 1;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none min-w-0">
            <button
              type="button"
              onClick={() => done && setStep(idx)}
              className={`group flex items-center gap-2.5 ${done ? "cursor-pointer" : active ? "" : "cursor-default"}`}
              data-testid={`co-step-${s.key}`}
            >
              <div className={`relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                active ? "bg-zinc-900 text-white shadow-[0_0_0_4px_rgba(24,24,27,0.06)]"
                : done ? "bg-zinc-900 text-white"
                : "bg-zinc-100 text-zinc-400"
              }`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3 h-3" />}
              </div>
              <div className="min-w-0 hidden sm:block">
                <div className={`text-[9px] uppercase tracking-[0.12em] font-medium ${active || done ? "text-zinc-500" : "text-zinc-400"}`}>Step {idx + 1}</div>
                <div className={`text-[12px] font-medium leading-tight truncate ${active ? "text-zinc-900" : done ? "text-zinc-700" : "text-zinc-400"}`}>{s.label}</div>
              </div>
            </button>
            {!isLast && (
              <div className="flex-1 mx-3 h-px bg-zinc-200 relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 bg-zinc-900 transition-all duration-500 ${done ? "w-full" : "w-0"}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Initiator metadata for the header — gives operational seriousness.
  const initiatedAt = useMemo(() => new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }), []);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] p-0 flex flex-col overflow-hidden gap-0 [&>button]:hidden"
        data-testid="co-wizard-dialog"
      >
        {/* ── Premium Header ───────────────────────────────────────────── */}
        <div className="relative px-7 pt-7 pb-5 border-b border-zinc-200/80">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-5 right-5 w-7 h-7 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
            data-testid="co-header-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 text-white flex items-center justify-center shrink-0">
              <Layers className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[19px] font-light tracking-tight text-zinc-900 leading-tight flex items-center gap-2">
                {isEditing
                  ? `Edit ${requestType === "upsell" ? "Upsell" : "Change Order"}${editingCo?.short_id ? ` · ${editingCo.short_id}` : ""}`
                  : forcedType === "upsell" ? "New Upsell" : forcedType === "change_order" ? "New Change Order" : "Additional Scope Intake"}
                {isPmRole && (
                  <Badge variant="secondary" className="text-[9px] gap-1 h-5 px-1.5 font-medium"><Lock className="w-2.5 h-2.5" /> PM View</Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-[12.5px] text-zinc-500 mt-0.5 leading-relaxed">
                {isEditing
                  ? "Change the scope, effort, or wording. Pricing is recalculated on save, and the client's live link reflects it immediately."
                  : forcedType === "upsell"
                  ? "Build an upsell proposal with the TRP pricing engine and send it to the client."
                  : "Capture the change, define deliverables, and send the approval link to the client."}
              </DialogDescription>
              <div className="mt-2.5 flex items-center gap-2 text-[10.5px] text-zinc-400 font-medium">
                <span>
                  {isEditing ? "Raised by " : "Initiated by "}
                  <span className="text-zinc-600">
                    {isEditing
                      ? (editingCo?.pm_name || editingCo?.created_by_email || "—")
                      : (me?.full_name || me?.name || me?.email || "—")}
                  </span>
                </span>
                <span className="text-zinc-300">•</span>
                <span>
                  {isEditing
                    ? `${(editingCo?.status || "").replace(/_/g, " ")}${editingCo?.revision_count ? ` · rev ${editingCo.revision_count}` : ""}`
                    : initiatedAt}
                </span>
                <span className="text-zinc-300">•</span>
                <span>Operational Revenue Workflow</span>
              </div>
            </div>
          </div>
        </div>

        <StepHeader />

        {/* ── Editing a row that already went out, or already took money ── */}
        {isEditing && (needsRepriceConfirm || editingCo?.client_approved) && (
          <div className="mx-7 mt-1 mb-1 rounded-md border border-orange-200 bg-orange-50 px-3 py-2.5 text-[11.5px] text-orange-800 leading-relaxed" data-testid="co-edit-warning">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="min-w-0">
                {needsRepriceConfirm ? (
                  <>
                    A payment is already recorded against this {requestType === "upsell" ? "upsell" : "change order"}.
                    Changing the scope re-prices it, which moves what is still owed.
                    {isFinance ? (
                      <label className="mt-2 flex items-center gap-2 font-medium cursor-pointer">
                        <input
                          type="checkbox"
                          checked={repriceConfirmed}
                          onChange={(e) => setRepriceConfirmed(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-orange-400 accent-orange-600"
                          data-testid="co-reprice-confirm"
                        />
                        Yes — re-price it and adjust the outstanding balance.
                      </label>
                    ) : (
                      <span className="block mt-1 font-medium">Ask finance to make this change.</span>
                    )}
                  </>
                ) : (
                  isPmRole ? (
                    <>The client has already approved this scope. Any change here is logged against the approval.</>
                  ) : (
                    <>The client has already approved this at {formatCurrency(editingCo?.final_client_price || editingCo?.total_amount)}. Any change here is logged against the approval.</>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Scrollable Body ──────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-7 py-5">
        {/* ── Step 1 — Premium intake (type → project → ownership) ────── */}
        {step === 0 && (
          <div className="space-y-7" data-testid="co-step-1">

            {/* SECTION A — Scope Type (hidden when type is pre-locked by parent) */}
            <section className={forcedType ? "hidden" : ""}>
              <div className="mb-2.5">
                <h3 className="text-[14px] font-semibold tracking-tight text-zinc-900">What are you initiating?</h3>
                <p className="text-[12px] text-zinc-500 mt-0.5">Choose the workflow that matches the request — this drives pricing, approvals, and routing.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <ScopeTypeCard
                  active={requestType === "change_order"}
                  onClick={() => { setRequestType("change_order"); setLineItems([]); }}
                  icon={RefreshCw}
                  title="Change Order"
                  caption="Modify or extend existing approved production scope."
                  bullets={["Revisions", "Additional effort", "Timeline impact", "Production-linked changes"]}
                  testId="co-type-change_order"
                />
                <ScopeTypeCard
                  active={requestType === "upsell"}
                  onClick={() => { setRequestType("upsell"); setLineItems([]); }}
                  icon={TrendingUp}
                  title="Upsell"
                  caption="Add new billable deliverables using TRP pricing logic."
                  bullets={["New renderings", "VEXOS add-ons", "Marketing upgrades", "New experiences"]}
                  testId="co-type-upsell"
                />
              </div>

              {requestType === "change_order" && (
                <div className="mt-3 px-3 py-2 rounded-md bg-blue-50/70 border border-blue-100 text-[11.5px] text-blue-900 leading-relaxed flex items-start gap-2">
                  <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
                  <span>This workflow is designed for modifications or additions tied to existing production commitments.</span>
                </div>
              )}
              {requestType === "upsell" && (
                <div className="mt-3 px-3 py-2 rounded-md bg-violet-50/70 border border-violet-100 text-[11.5px] text-violet-900 leading-relaxed flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
                  <span>This workflow opens the TRP pricing engine to build additional billable deliverables.</span>
                </div>
              )}
            </section>

            {/* SECTION B — Project context */}
            <section>
              <div className="mb-2.5">
                <h3 className="text-[14px] font-semibold tracking-tight text-zinc-900">Which project is this tied to?</h3>
                <p className="text-[12px] text-zinc-500 mt-0.5">Search by project or client. The assigned PM and Sales Rep will be auto-populated.</p>
              </div>
              <ProjectCommandPicker
                programs={filteredPrograms}
                allCount={programs.length}
                query={projectQuery}
                onQuery={setProjectQuery}
                selectedId={selectedProjectId}
                onSelect={(id) => setSelectedProjectId(id)}
                users={allUsers}
              />
              {selectedProject && (
                <div className="mt-2 px-3 py-2 rounded-md bg-zinc-50 border border-zinc-200 flex items-start gap-2.5 text-[11.5px] text-zinc-600 flex-wrap">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div>Selected: <span className="text-zinc-900 font-medium">{getProjectDisplayName(selectedProject)}</span></div>
                    <div className="mt-0.5">
                      Client: <span className="text-zinc-700">{selectedProject.client_name || selectedProject.clientName || "—"}</span>
                      <span className="text-zinc-300 mx-1.5">•</span>
                      PM: <span className="text-zinc-700">{getProjectPMDisplayName(selectedProject, allUsers)}</span>
                      <span className="text-zinc-300 mx-1.5">•</span>
                      Status: <span className="text-zinc-700 capitalize">{(selectedProject.status || "Active").toString().replace(/_/g, " ")}</span>
                      {salesRepName && <>
                        <span className="text-zinc-300 mx-1.5">•</span>
                        Sales Rep: <span className="text-zinc-700">{salesRepName}</span>
                      </>}
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* SECTION C — Ownership & Routing */}
            <section>
              <div className="mb-2.5">
                <h3 className="text-[14px] font-semibold tracking-tight text-zinc-900">Client Details</h3>
                <p className="text-[12px] text-zinc-500 mt-0.5">
                  {requestType === "change_order"
                    ? "Add the client email so the approval & payment link can be sent directly."
                    : "Add the upsell title, client email, and assigned sales rep."}
                </p>
              </div>

              {requestType === "change_order" ? (
                <div className="space-y-3">
                  {/* Auto-filled email chip */}
                  {clientEmail && !emailEditing ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5 flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <Mail className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-emerald-700 font-medium uppercase tracking-wide">
                          {emailSource === "project" ? "From project record" : emailSource === "intake" ? "From intake submission" : "Client email"}
                        </div>
                        <div className="text-[13.5px] font-medium text-zinc-900 truncate mt-0.5">{clientEmail}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEmailEditing(true)}
                        className="text-[11.5px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2 shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-medium text-zinc-700">Client Email</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="client@example.com"
                          value={clientEmail}
                          onChange={(e) => { setClientEmail(e.target.value); setEmailSource("manual"); }}
                          className="h-9 text-[13px] flex-1"
                          data-testid="co-client-email"
                          autoFocus={emailEditing}
                        />
                        {emailEditing && (
                          <Button type="button" size="sm" variant="outline" className="h-9 text-[12px] shrink-0 border-zinc-200" onClick={() => setEmailEditing(false)}>
                            Done
                          </Button>
                        )}
                      </div>
                      {!selectedProjectId && (
                        <p className="text-[10.5px] text-zinc-400">Select a project first — the client email will be pulled automatically.</p>
                      )}
                    </div>
                  )}

                  {/* CC email */}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-zinc-700">CC <span className="text-zinc-400 font-normal">(optional)</span></Label>
                    <Input
                      type="email"
                      placeholder="cc@example.com"
                      value={ccEmail}
                      onChange={(e) => setCcEmail(e.target.value)}
                      className="h-9 text-[13px]"
                      data-testid="co-cc-email"
                    />
                    <p className="text-[10.5px] text-zinc-400">Add another person to receive the approval email.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Upsell title */}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-zinc-700">Upsell Title <span className="text-rose-500">*</span></Label>
                    <Input
                      placeholder="e.g. Additional Residence 360 Tour Package"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="h-9 text-[13px]"
                      data-testid="co-title"
                    />
                  </div>

                  {/* Smart email chip — same pattern as change orders */}
                  {clientEmail && !emailEditing ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3.5 py-2.5 flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <Mail className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-emerald-700 font-medium uppercase tracking-wide">
                          {emailSource === "project" ? "From project record" : emailSource === "intake" ? "From intake submission" : "Client email"}
                        </div>
                        <div className="text-[13.5px] font-medium text-zinc-900 truncate mt-0.5">{clientEmail}</div>
                      </div>
                      <button type="button" onClick={() => setEmailEditing(true)} className="text-[11.5px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2 shrink-0">Change</button>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-medium text-zinc-700">Client Email</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="client@example.com"
                          value={clientEmail}
                          onChange={(e) => { setClientEmail(e.target.value); setEmailSource("manual"); }}
                          className="h-9 text-[13px] flex-1"
                          data-testid="co-client-email"
                          autoFocus={emailEditing}
                        />
                        {emailEditing && (
                          <Button type="button" size="sm" variant="outline" className="h-9 text-[12px] shrink-0 border-zinc-200" onClick={() => setEmailEditing(false)}>Done</Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CC */}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-zinc-700">CC <span className="text-zinc-400 font-normal">(optional)</span></Label>
                    <Input type="email" placeholder="cc@example.com" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} className="h-9 text-[13px]" data-testid="co-cc-email" />
                  </div>

                  {/* Sales Rep */}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-zinc-700">Assigned Sales Rep</Label>
                    <SalesRepSelect
                      salespeople={salespeople}
                      salesRepId={salesRepId}
                      onSelect={(u) => {
                        setSalesRepId(u.id);
                        setSalesRepName(u.full_name || u.name || "");
                        setSalesRepEmail(u.email || "");
                      }}
                    />
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {/* ── Step 2 — Change Order Scope (simple) ──────────────────────── */}
        {step === 1 && requestType === "change_order" && (
          <div className="space-y-6" data-testid="co-step-2">

            {/* SECTION A — Category + Summary */}
            <Section
              title="Scope Overview"
              caption="Choose the category and describe what needs to change."
            >
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <FieldLabel>Category</FieldLabel>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="co-category" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {category === "other" && (
                    <Input className="mt-1.5 h-9 text-[13px]" placeholder="Custom category name…" value={categoryCustom} onChange={(e) => setCategoryCustom(e.target.value)} />
                  )}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <FieldLabel>
                      Scope Summary {isItemized
                        ? <span className="text-zinc-400 font-normal">(optional — shown above the itemized breakdown)</span>
                        : <span className="text-rose-500">*</span>}
                    </FieldLabel>
                    <EnhanceButton />
                  </div>
                  <Textarea
                    rows={isItemized ? 2 : 3}
                    placeholder={isItemized
                      ? "One or two sentences on why the scope is changing."
                      : "Type it rough — AI can formalize it. e.g. client wants 2 more bedroom renders + new lobby furniture"}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onFocus={(e) => { e.target.rows = 5; }}
                    onBlur={(e) => { if (!e.target.value) e.target.rows = 3; }}
                    className="text-[13px] leading-[1.65] resize-none border-zinc-200 focus-visible:border-zinc-400 focus-visible:ring-zinc-200/40"
                    data-testid="co-description"
                  />
                </div>
              </div>
            </Section>

            {/* SECTION B — Deliverables */}
            <Section
              title={isItemized ? "Scope Items" : "Deliverables"}
              caption={isItemized
                ? "Break the change into separate items. The client sees each one with its own price and can approve all of them or only some."
                : "List each item being changed or added."}
              action={
                <Button size="sm" variant="outline" type="button" onClick={addCustomLineItem} className="h-7 text-[11.5px] gap-1 border-zinc-200 hover:bg-zinc-50" data-testid="co-add-line-item">
                  <Plus className="w-3 h-3" /> Add Item
                </Button>
              }
            >
              {lineItems.length === 0 ? (
                <button
                  type="button"
                  onClick={addCustomLineItem}
                  className="w-full rounded-xl border border-dashed border-zinc-300 bg-zinc-50/40 px-6 py-7 text-center hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                >
                  <Plus className="w-4 h-4 mx-auto text-zinc-400 mb-1.5" />
                  <div className="text-[13px] font-medium text-zinc-600">{isItemized ? "Add the first scope item" : "Add first deliverable"}</div>
                  <div className="text-[11.5px] text-zinc-400 mt-0.5">
                    {isItemized ? "e.g. Site & masterplan adjustments, orbit re-rendering, VEXOS updates" : "Name it and pick the type of change"}
                  </div>
                </button>
              ) : (
                <COScopeItemsEditor
                  items={lineItems}
                  onUpdate={updateLineItem}
                  onRemove={removeLineItem}
                  itemized={isItemized}
                  showPrice={isItemized && !isPmRole}
                  priceOf={itemPrice}
                />
              )}
              {isItemized && !isPmRole && lineItems.length > 0 && (
                <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center justify-between gap-4" data-testid="co-scope-running-total">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">Change Order Total</div>
                    <div className="text-[10.5px] text-zinc-400 mt-0.5">
                      {lineItems.length} item{lineItems.length !== 1 ? "s" : ""} · a blank price is calculated from the production estimate in the next step
                    </div>
                  </div>
                  <div className="text-[22px] font-light tabular-nums tracking-tight text-zinc-900 shrink-0">{formatCurrency(scopeTotals.proposed)}</div>
                </div>
              )}
            </Section>
          </div>
        )}

        {/* ── Step 2 — Upsell Mini Calculator (premium revenue configurator) ── */}
        {step === 1 && requestType === "upsell" && (
          <div className="space-y-7" data-testid="co-step-2-upsell">

            {/* SECTION A — Service Catalog */}
            <Section
              title="Service Catalog"
              caption="Configurable production deliverables, sourced from the TRP rate card."
            >
              <UpsellCatalogPicker catalog={upsellCatalog} onAdd={addCatalogItem} isPmRole={isPmRole} />
            </Section>

            {/* SECTION B — Selected Revenue Scope */}
            <Section
              title="Selected Revenue Scope"
              caption="Configured deliverables that will become additional client commitments upon approval."
              action={
                <Button size="sm" variant="outline" type="button" onClick={addCustomLineItem} className="h-7 text-[11.5px] gap-1 border-zinc-200 hover:bg-zinc-50" data-testid="co-add-custom-line-item">
                  <Plus className="w-3 h-3" /> Add Custom Item
                </Button>
              }
            >
              {lineItems.length === 0 ? (
                <RevenueScopeEmptyState onAdd={addCustomLineItem} />
              ) : (
                <LineItemsEditor
                  items={lineItems}
                  isPmRole={isPmRole}
                  onUpdate={updateLineItem}
                  onRemove={removeLineItem}
                  projectCommitments={projectCommitments}
                />
              )}

              {!isPmRole && lineItemsTotal > 0 && (
                <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 flex items-center justify-between" data-testid="co-pricing-preview">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">Estimated Revenue</div>
                    <div className="text-[10.5px] text-zinc-400 mt-0.5">{lineItems.length} deliverable{lineItems.length !== 1 ? "s" : ""} · pre-discount</div>
                  </div>
                  <div className="text-[24px] font-light tabular-nums tracking-tight text-zinc-900">{formatCurrency(lineItemsTotal)}</div>
                </div>
              )}
            </Section>

            {/* SECTION C — Internal Operational Notes */}
            <Section
              title="Internal Operational Notes"
              caption="Visible internally to Sales, Production, and Operations only."
              muted
            >
              <Textarea
                rows={2}
                placeholder="Pricing rationale, client context, or production caveats…"
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                className="text-[12.5px] leading-[1.6] resize-none bg-zinc-50/40 border-zinc-200 focus-visible:bg-white focus-visible:border-zinc-400 focus-visible:ring-zinc-200/40"
                data-testid="co-internal-notes"
              />
            </Section>

            {/* SECTION D — Smart Upsell Opportunities (collapsed by default) */}
            {selectedProjectId && (
              <div className="rounded-xl border border-zinc-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSmartUpsellOpen(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50/60 hover:bg-zinc-100/60 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-[13px] font-medium text-zinc-800">Smart Upsell Opportunities</span>
                    <span className="text-[11px] text-zinc-400">AI-ranked recommendations</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${smartUpsellOpen ? "rotate-90" : ""}`} />
                </button>
                {smartUpsellOpen && (
                  <div className="p-4 border-t border-zinc-200">
                    <SmartUpsellPanel
                      programId={selectedProjectId}
                      programName={selectedProject ? getProjectDisplayName(selectedProject) : null}
                      onAddToProposal={(li) => {
                        setLineItems(prev => [...prev, { id: crypto.randomUUID(), ...li }]);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3 — Production Method (Change Order) OR Timeline (Upsell) ── */}
        {step === 2 && requestType === "change_order" && (
          <div className="space-y-4" data-testid="co-step-3">
            {isItemized && (
              <div className="space-y-3" data-testid="co-item-production">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-[14px] font-semibold tracking-tight text-zinc-900">Production per item</h3>
                    <p className="text-[12px] text-zinc-500 mt-0.5 leading-relaxed">
                      {isPmRole
                        ? "Estimate the hours, artists, or studio for each item. Pricing is calculated from your estimate."
                        : "Estimate each item's hours, artists, or studio cost. Each item's price comes from its estimate unless you type one."}
                    </p>
                  </div>
                  {anyOutsourcing && (
                    <label className="flex items-center gap-2 text-[11.5px] text-zinc-600 shrink-0 cursor-pointer">
                      <input type="checkbox" checked={outsourceManagerContacted} onChange={(e) => setOutsourceManagerContacted(e.target.checked)} />
                      Outsource manager contacted
                    </label>
                  )}
                </div>
                {lineItems.map((li, idx) => (
                  <ItemProductionCard
                    key={li.id}
                    item={li}
                    index={idx}
                    isPmRole={isPmRole}
                    recommended={itemRecommended(li)}
                    ready={itemProductionReady(li)}
                    multiplierFor={getOutsourceMultiplier}
                    onChange={(patch) => updateLineItem(li.id, patch)}
                  />
                ))}
              </div>
            )}

            {!isItemized && (<>
            <div className="grid grid-cols-3 gap-2">
              {PRODUCTION_METHODS.map(m => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setProductionMethod(m.value)}
                  className={`p-3 border rounded-md text-left transition-colors ${productionMethod === m.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`}
                  data-testid={`co-method-${m.value}`}
                >
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground">{m.sub}</div>
                </button>
              ))}
            </div>

            {(productionMethod === "in_house" || productionMethod === "hybrid") && (
              <div className="space-y-3 p-3 border border-border rounded-md">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">In-House Effort</div>

                {inHouseEfforts.map((effort, idx) => (
                  <div key={idx} className="space-y-2 p-2.5 rounded-md bg-muted/20 border border-border/60">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                        Effort {idx + 1}
                      </span>
                      {inHouseEfforts.length > 1 && (
                        <button type="button" onClick={() => removeEffort(idx)} className="text-xs text-destructive hover:underline">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Estimated Hours</Label>
                        <Input type="number" min="0" step="0.25" value={effort.estimatedHours} onChange={(e) => updateEffort(idx, "estimatedHours", e.target.value)} data-testid={`co-hours-${idx}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Artist Type</Label>
                        <Select value={effort.artistType} onValueChange={(v) => updateEffort(idx, "artistType", v)}>
                          <SelectTrigger data-testid={`co-artist-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ARTIST_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addEffort}
                  className="w-full py-1.5 text-xs font-medium border border-dashed border-border rounded-md text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                >
                  + Add another effort
                </button>

                {totalInHouseHours > 0 && (
                  <div className="text-xs text-muted-foreground px-1">
                    Total estimated hours: <strong>{totalInHouseHours}h</strong>
                  </div>
                )}
              </div>
            )}

            {(productionMethod === "outsourced" || productionMethod === "hybrid") && (
              <div className="space-y-3 p-3 border border-border rounded-md">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Outsource Studios</div>
                  <div className="flex items-center gap-2 text-sm">
                    <input type="checkbox" id="co-outsource-contacted" checked={outsourceManagerContacted} onChange={(e) => setOutsourceManagerContacted(e.target.checked)} />
                    <Label htmlFor="co-outsource-contacted" className="cursor-pointer text-xs">Manager contacted</Label>
                  </div>
                </div>

                {vendors.map((studio, idx) => (
                  <div key={idx} className="space-y-2 p-2.5 rounded-md bg-muted/20 border border-border/60">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                        Studio {idx + 1}
                      </span>
                      {vendors.length > 1 && (
                        <button type="button" onClick={() => removeVendor(idx)} className="text-xs text-destructive hover:underline">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Studio Name</Label>
                        <Input value={studio.name} onChange={(e) => updateVendor(idx, "name", e.target.value)} placeholder="e.g. Studio Atlas" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Quote {!isPmRole && <span className="text-muted-foreground">(cost)</span>}</Label>
                        {isPmRole ? (
                          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 rounded">Finalized by Sales.</div>
                        ) : (
                          <div className="flex gap-1">
                            <Input type="number" min="0" step="100" value={studio.cost} onChange={(e) => updateVendor(idx, "cost", e.target.value)} placeholder="0" />
                            <Select value={studio.currency} onValueChange={(v) => updateVendor(idx, "currency", v)}>
                              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="USD">USD</SelectItem>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="GBP">GBP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Scope of Work <span className="text-muted-foreground">(what this studio is responsible for)</span></Label>
                      <Textarea
                        value={studio.scope}
                        onChange={(e) => updateVendor(idx, "scope", e.target.value)}
                        placeholder="e.g. Exterior renders — Buildings A & B"
                        rows={2}
                        className="text-xs resize-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Timeline</Label>
                      <Input value={studio.timeline} onChange={(e) => updateVendor(idx, "timeline", e.target.value)} placeholder="e.g. 10 business days" />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addVendor}
                  className="w-full py-1.5 text-xs font-medium border border-dashed border-border rounded-md text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                >
                  + Add another studio
                </button>

                {!isPmRole && totalVendorCost > 0 && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                    <span>Total vendor cost: <strong>${totalVendorCost.toLocaleString()}</strong></span>
                    <span>Multiplier: <strong>{getOutsourceMultiplier(totalVendorCost)}x</strong> (auto-tiered)</span>
                  </div>
                )}
              </div>
            )}
            </>)}

            {/* Urgency + timeline apply to every production method, including outsourced */}
            <div className="space-y-3 p-3 border border-border rounded-md">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Urgency &amp; Timeline</div>
              <div className="space-y-1.5">
                <Label>Urgency</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger data-testid="co-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {URGENCY.map(u => <SelectItem key={u.value} value={u.value}>{u.label} · {u.mult}×</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Desired Completion Date</Label>
                  <Input type="date" value={desiredCompletionDate} onChange={(e) => setDesiredCompletionDate(e.target.value)} data-testid="co-completion-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Timeline Impact</Label>
                  <Select value={timelineImpact} onValueChange={setTimelineImpact}>
                    <SelectTrigger data-testid="co-timeline-impact"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMELINE_IMPACTS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {isItemized && !isPmRole && (
              <div className="rounded-md bg-primary/5 border border-primary/20 px-4 py-3 flex items-start justify-between gap-4" data-testid="co-pricing-preview">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Change Order Total</div>
                  <div className="text-2xl font-light tabular-nums">{formatCurrency(scopeTotals.billed)}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Sum of {billedItems.length} item{billedItems.length !== 1 ? "s" : ""}
                    {Math.abs(scopeTotals.billed - scopeTotals.recommended) > 0.01 && <> · recommended {formatCurrency(scopeTotals.recommended)}</>}
                    {billedItems.length < lineItems.length && <> · {lineItems.length - billedItems.length} declined by the client</>}
                  </div>
                </div>
                {marginEstimate !== null && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Estimated Margin</div>
                    <div className="text-base font-medium tabular-nums">{formatCurrency(marginEstimate)}</div>
                  </div>
                )}
              </div>
            )}

            {!isItemized && !isPmRole && (computedRecommendedPrice > 0 || priceOverrideOn) && (
              <ClientPriceCard
                testId="co-pricing-preview"
                label="Recommended Client Price"
                recommended={computedRecommendedPrice}
                overrideOn={priceOverrideOn}
                overrideValue={priceOverride}
                isOverridden={isOverridden}
                marginEstimate={marginEstimate}
                onEnable={() => { setPriceOverrideOn(true); setPriceOverride(String(computedRecommendedPrice ?? "")); }}
                onDisable={() => { setPriceOverrideOn(false); setPriceOverride(""); }}
                onChange={setPriceOverride}
              />
            )}
          </div>
        )}

        {/* Upsell step 3 = Timeline + Notes only */}
        {step === 2 && requestType === "upsell" && (
          <div className="space-y-4" data-testid="co-step-3-upsell">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desired Completion Date</Label>
                <Input type="date" value={desiredCompletionDate} onChange={(e) => setDesiredCompletionDate(e.target.value)} data-testid="co-completion-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Timeline Impact</Label>
                <Select value={timelineImpact} onValueChange={setTimelineImpact}>
                  <SelectTrigger data-testid="co-timeline-impact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMELINE_IMPACTS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Description / Cover Note</Label>
                <EnhanceButton />
              </div>
              <Textarea rows={3} placeholder="A short description of the upsell to include in the client-facing proposal." value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label>Attachments</Label>
              <label className="flex items-center justify-center gap-2 px-3 py-3 border border-dashed border-border rounded-md cursor-pointer hover:bg-muted/30 text-sm text-muted-foreground">
                <Upload className="w-4 h-4" />
                Upload references, mockups, contracts…
                <input type="file" multiple className="hidden" onChange={handleFileSelect} />
              </label>
              {attachments.length > 0 && (
                <div className="space-y-1 mt-2">
                  {attachments.map((a, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-muted/40 rounded text-xs">
                      <span className="flex items-center gap-1.5 min-w-0"><Paperclip className="w-3 h-3 shrink-0" /><span className="truncate">{a.filename}</span></span>
                      <button type="button" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isPmRole && (lineItemsTotal > 0 || priceOverrideOn) && (
              <ClientPriceCard
                testId="co-pricing-preview"
                label="Upsell Total"
                recommended={computedRecommendedPrice}
                overrideOn={priceOverrideOn}
                overrideValue={priceOverride}
                isOverridden={isOverridden}
                onEnable={() => { setPriceOverrideOn(true); setPriceOverride(String(computedRecommendedPrice ?? "")); }}
                onDisable={() => { setPriceOverrideOn(false); setPriceOverride(""); }}
                onChange={setPriceOverride}
              />
            )}
          </div>
        )}

        {/* ── Step 4 — Review ─────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-3" data-testid="co-step-4">
            <ReviewRow label="Project" value={getProjectDisplayName(selectedProject)} />
            <ReviewRow label="Client" value={selectedProject?.client_name || selectedProject?.clientName || "—"} />
            <ReviewRow label="Type" value={requestType === "upsell" ? "Upsell" : "Change Order"} />
            {requestType === "upsell" && <ReviewRow label="Title" value={title} />}
            {requestType === "upsell" && salesRepName && <ReviewRow label="Sales Rep" value={salesRepName} />}
            <hr className="border-border/50" />
            {requestType === "change_order" && (
              <>
                <ReviewRow label="Category" value={CATEGORIES.find(c => c.value === category)?.label} />
                <ReviewRow label="Description" value={description} multiline />
              </>
            )}
            {isItemized ? (
              <ItemizedReviewTable items={lineItems} isPmRole={isPmRole} priceOf={itemPrice} />
            ) : (
              <ReviewRow label="Line Items" value={`${lineItems.length} item${lineItems.length !== 1 ? "s" : ""}`} />
            )}
            {linkedCommitmentIds.length > 0 && <ReviewRow label="Linked Commitments" value={`${linkedCommitmentIds.length}`} />}
            {internalNotes && <ReviewRow label="Internal Notes" value={internalNotes} multiline />}
            <ReviewRow label="Attachments" value={`${attachments.length} file${attachments.length !== 1 ? "s" : ""}`} />
            <hr className="border-border/50" />
            {isItemized && <ReviewRow label="Urgency" value={URGENCY.find(u => u.value === urgency)?.label || urgency} />}
            {orderLevelProduction && (
              <>
                <ReviewRow label="Production Method" value={productionMethod.replace("_", "-")} />
                {(productionMethod === "in_house" || productionMethod === "hybrid") && (
                  <>
                    {inHouseEfforts.map((e, i) => (
                      <ReviewRow
                        key={i}
                        label={inHouseEfforts.length > 1 ? `Effort ${i + 1}` : "Estimated Hours"}
                        value={`${e.estimatedHours || 0} h — ${ARTIST_TYPES.find(a => a.value === e.artistType)?.label || e.artistType}`}
                      />
                    ))}
                  </>
                )}
                <ReviewRow label="Urgency" value={URGENCY.find(u => u.value === urgency)?.label || urgency} />
                {(productionMethod === "outsourced" || productionMethod === "hybrid") && (
                  <>
                    {vendors.map((s, i) => (
                      <ReviewRow key={i} label={vendors.length > 1 ? `Studio ${i + 1}` : "Vendor"} value={`${s.name}${!isPmRole && s.cost ? ` — ${s.currency} ${s.cost}` : ""}`} />
                    ))}
                  </>
                )}
              </>
            )}
            <ReviewRow label="Desired Completion" value={desiredCompletionDate || "—"} />
            <ReviewRow label="Timeline Impact" value={timelineImpact} />
            {!isPmRole && (effectivePrice > 0 || isOverridden) && (
              <>
                <hr className="border-border/50" />
                <ReviewRow
                  label={isItemized ? "Change Order Total" : isOverridden ? "Client Price (custom)" : requestType === "upsell" ? "Upsell Total" : "Recommended Price"}
                  value={formatCurrency(effectivePrice)}
                />
                {isOverridden && <ReviewRow label="Recommended" value={formatCurrency(computedRecommendedPrice)} />}
                {marginEstimate !== null && <ReviewRow label="Estimated Margin" value={formatCurrency(marginEstimate)} />}
              </>
            )}
            {requestType === "change_order" ? (
              <div className="bg-blue-50/70 rounded-md px-3 py-2.5 text-xs text-blue-900 border border-blue-100 mt-2 flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500" />
                <span>After submitting, you'll preview the client email and send the approval &amp; payment link directly.</span>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-md px-3 py-2 text-xs text-muted-foreground mt-2">
                Once submitted, this upsell goes to <strong className="text-foreground">{salesRepName || "Sales"}</strong> for review and pricing approval.
              </div>
            )}
          </div>
        )}

        {/* ── Step 5 — Email Preview (Change Orders only) ─────────────── */}
        {step === 4 && requestType === "change_order" && (
          <div className="space-y-5" data-testid="co-step-5">
            {emailSent ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-6 py-8 text-center">
                <div className="text-4xl mb-3">✓</div>
                <div className="text-[16px] font-light text-emerald-900">Email sent to client</div>
                <div className="text-[12.5px] text-emerald-700 mt-1.5 leading-relaxed">
                  {clientEmail
                    ? <>Sent to <span className="font-medium">{clientEmail}</span>. They'll receive the approval &amp; payment link.</>
                    : "The client will receive the approval & payment link."}
                </div>
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={handleCopyApprovalLink}
                    className="flex items-center gap-1.5 text-[12px] text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
                  >
                    <Copy className="w-3 h-3" /> Copy approval link
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-zinc-900 text-white flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold tracking-tight text-zinc-900">Email Preview</div>
                      <div className="text-[11.5px] text-zinc-500">Review and edit before sending to the client</div>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-xl border border-zinc-200 bg-zinc-50/40 p-4">
                    {clientEmail && (
                      <div className="flex items-center gap-2 text-[11.5px] text-zinc-600 pb-2 border-b border-zinc-200">
                        <Mail className="w-3 h-3 text-zinc-400 shrink-0" />
                        <span className="text-zinc-400">To:</span>
                        <span className="font-medium text-zinc-700">{clientEmail}</span>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-medium text-zinc-600">Subject</Label>
                      <Input
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="h-9 text-[13px] bg-white"
                        data-testid="co-email-subject"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-medium text-zinc-600">Message</Label>
                      <Textarea
                        rows={11}
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        className="text-[12.5px] leading-relaxed resize-none bg-white"
                        data-testid="co-email-body"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={handleSendEmail}
                    disabled={emailSending || !createdCo}
                    className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5"
                    data-testid="co-send-email-btn"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {emailSending ? "Sending…" : "Send to Client"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyApprovalLink}
                    disabled={!createdCo?.public_token}
                    className="h-8 text-[12.5px] gap-1.5 border-zinc-200"
                    data-testid="co-copy-link-btn"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy Approval Link
                  </Button>
                </div>

                {!clientEmail && (
                  <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11.5px] text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    No client email provided. Copy the approval link and share it manually.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        </div>{/* end scrollable body */}

        {/* ── Sticky Footer ────────────────────────────────────────────── */}
        <DialogFooter className="px-7 py-3.5 border-t border-zinc-200/80 bg-white/90 backdrop-blur-sm flex-row !justify-between gap-2 shrink-0">
          <div>
            {step > 0 && step < 4 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} disabled={saving} className="h-8 text-[12.5px] text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100" data-testid="co-back-btn">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Cancel — hidden on email step after sending */}
            {!(step === 4 && emailSent) && (
              <Button variant="ghost" size="sm" onClick={() => { onOpenChange(false); if (step === 4) { onSaved?.(); reset(); } }} disabled={saving || emailSending} className="h-8 text-[12.5px] text-zinc-500 hover:text-zinc-900" data-testid="co-cancel-btn">
                {step === 4 ? "Skip & Close" : "Cancel"}
              </Button>
            )}

            {/* Editing — save from wherever you are; no send step to reach. */}
            {isEditing && (
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={saving || (needsRepriceConfirm && !(isFinance && repriceConfirmed))}
                className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5 disabled:opacity-40"
                data-testid="co-save-edit-btn"
              >
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            )}

            {/* Steps 0-2: Continue */}
            {step < 3 && (
              <Button
                size="sm"
                onClick={() => setStep(step + 1)}
                disabled={(step === 0 && !canAdvanceStep1) || (step === 1 && !canAdvanceStep2) || (step === 2 && !canAdvanceStep3)}
                className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5 group disabled:opacity-40"
                data-testid="co-next-btn"
              >
                Continue <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            )}

            {/* Step 3 — change order: Submit & preview email */}
            {step === 3 && !isEditing && requestType === "change_order" && (
              <Button size="sm" onClick={handleSubmit} disabled={saving} className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5" data-testid="co-submit-btn">
                {saving ? "Creating…" : <><Mail className="w-3.5 h-3.5" /> Submit &amp; Preview Email</>}
              </Button>
            )}

            {/* Step 3 — upsell: Submit */}
            {step === 3 && !isEditing && requestType === "upsell" && (
              <Button size="sm" onClick={handleSubmit} disabled={saving} className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5" data-testid="co-submit-btn">
                {saving ? "Submitting…" : "Submit Additional Scope"}
              </Button>
            )}

            {/* Step 4 — email sent: Done */}
            {step === 4 && emailSent && (
              <Button size="sm" onClick={() => { onSaved?.(); onOpenChange(false); reset(); }} className="h-8 text-[12.5px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5" data-testid="co-done-btn">
                Done
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The one card that decides what the client is billed. The engine's
// recommendation is always on it — an override is shown as a departure from a
// number that stays visible, never as a replacement that hides it.
function ClientPriceCard({
  label, recommended, overrideOn, overrideValue, isOverridden,
  marginEstimate = null, onEnable, onDisable, onChange, testId,
}) {
  return (
    <div className="rounded-md bg-primary/5 border border-primary/20 px-4 py-3 space-y-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {overrideOn ? "Custom Client Price" : label}
          </div>
          {overrideOn ? (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xl font-light text-muted-foreground">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={overrideValue}
                onChange={(e) => onChange(e.target.value)}
                placeholder={recommended ? String(recommended) : "0.00"}
                className="h-9 w-40 text-xl font-light tabular-nums"
                data-testid="co-price-override-input"
              />
            </div>
          ) : (
            <div className="text-2xl font-light tabular-nums">{formatCurrency(recommended)}</div>
          )}
        </div>
        {marginEstimate !== null && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Estimated Margin</div>
            <div className="text-base font-medium tabular-nums">{formatCurrency(marginEstimate)}</div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 text-[11px]">
        {overrideOn ? (
          <>
            <span className="text-muted-foreground tabular-nums">Recommended {formatCurrency(recommended)}</span>
            <button
              type="button"
              onClick={onDisable}
              className="underline underline-offset-2 text-muted-foreground hover:text-foreground"
              data-testid="co-price-override-clear"
            >
              Use recommended
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onEnable}
            className="underline underline-offset-2 text-muted-foreground hover:text-foreground"
            data-testid="co-price-override-toggle"
          >
            Set a custom price
          </button>
        )}
      </div>
      {overrideOn && !isOverridden && (
        <div className="text-[11px] text-amber-700" data-testid="co-price-override-hint">
          Type an amount to bill, or go back to the recommended price.
        </div>
      )}
    </div>
  );
}

function ReviewRow({ label, value, multiline = false }) {
  return (
    <div className={`grid grid-cols-3 gap-3 text-sm ${multiline ? "items-start" : "items-center"}`}>
      <span className="text-muted-foreground text-xs uppercase tracking-wide col-span-1">{label}</span>
      <span className={`col-span-2 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value || "—"}</span>
    </div>
  );
}

// ── Premium intake subcomponents ──────────────────────────────────────────
function ScopeTypeCard({ active, onClick, icon: Icon, title, caption, bullets, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group relative text-left p-4 rounded-xl border transition-all duration-200 ease-out
        ${active
          ? "border-zinc-900 bg-zinc-50/60 shadow-[0_0_0_4px_rgba(24,24,27,0.04)]"
          : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50/40 hover:-translate-y-px"}
      `}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 group-hover:bg-zinc-900 group-hover:text-white"}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className={`w-3.5 h-3.5 rounded-full border transition-all ${active ? "border-zinc-900 bg-zinc-900 shadow-[inset_0_0_0_2.5px_white]" : "border-zinc-300 bg-white"}`} />
      </div>
      <div className="text-[14px] font-semibold tracking-tight text-zinc-900">{title}</div>
      <div className="text-[11.5px] text-zinc-500 mt-0.5 leading-snug">{caption}</div>
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
        {bullets.map((b, i) => (
          <div key={i} className="text-[10.5px] text-zinc-500 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-zinc-300" /> {b}
          </div>
        ))}
      </div>
    </button>
  );
}

function ProjectCommandPicker({ programs, allCount, query, onQuery, selectedId, onSelect, users = [] }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-3 h-10 border-b border-zinc-200 bg-zinc-50/50">
        <Search className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <input
          autoComplete="off"
          placeholder="Search projects by name or client…"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] placeholder:text-zinc-400 outline-none border-0"
          data-testid="co-project-search"
        />
        <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[9px] font-medium text-zinc-400 bg-white border border-zinc-200 rounded px-1.5 py-0.5">⌘K</kbd>
      </div>
      <div className="max-h-[280px] overflow-y-auto">
        {programs.length === 0 && (
          <div className="px-4 py-8 text-[12px] text-zinc-400 text-center">
            No projects match your search.
          </div>
        )}
        {programs.map(p => {
          const id = p.id || p._id;
          const active = selectedId === id;
          const name = getProjectDisplayName(p);
          const initial = (name || "P").trim().charAt(0).toUpperCase();
          const status = (p.status || "Active").toString();
          const pm = getProjectPMDisplayName(p, users);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              data-testid={`co-project-option-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-zinc-100 last:border-b-0 transition-colors text-left
                ${active ? "bg-zinc-50" : "hover:bg-zinc-50/60"}`}
            >
              <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-semibold shrink-0 ${active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-zinc-900 truncate">{name}</div>
                <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1.5">
                  <span>Client: {p.client_name || p.clientName || "—"}</span>
                  <span className="text-zinc-300">•</span>
                  <span>PM: {pm}</span>
                  <span className="text-zinc-300">•</span>
                  <span className="capitalize">{status.replace(/_/g, " ")}</span>
                </div>
              </div>
              {active && <CheckCircle2 className="w-3.5 h-3.5 text-zinc-900 shrink-0" />}
            </button>
          );
        })}
      </div>
      {allCount > programs.length && (
        <div className="px-3 py-1.5 border-t border-zinc-100 text-[10.5px] text-zinc-400 bg-zinc-50/40">
          Showing {programs.length} of {allCount} projects · refine your search
        </div>
      )}
    </div>
  );
}

function SalesRepSelect({ salespeople, salesRepId, onSelect }) {
  const selected = salespeople.find(s => s.id === salesRepId);
  const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join("");
  return (
    <Select value={salesRepId} onValueChange={(id) => {
      const u = salespeople.find(s => s.id === id);
      if (u) onSelect(u);
    }}>
      <SelectTrigger data-testid="co-sales-rep" className="h-9 text-[13px]">
        {selected ? (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-5 h-5 rounded-full bg-zinc-900 text-white text-[9px] font-semibold flex items-center justify-center shrink-0">
              {initials(selected.full_name || selected.name)}
            </div>
            <span className="truncate text-zinc-900">{selected.full_name || selected.name}</span>
          </div>
        ) : (
          <SelectValue placeholder="Assign a sales rep…" />
        )}
      </SelectTrigger>
      <SelectContent>
        {salespeople.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">No sales reps loaded</div>}
        {salespeople.map(s => (
          <SelectItem key={s.id} value={s.id}>
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-semibold flex items-center justify-center">
                {initials(s.full_name || s.name)}
              </div>
              <div>
                <div className="text-[12.5px] font-medium text-zinc-900">{s.full_name || s.name}</div>
                <div className="text-[10.5px] text-zinc-500">{s.email}</div>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Reusable subcomponents ─────────────────────────────────────────────────
function UpsellCatalogPicker({ catalog, onAdd, isPmRole }) {
  const categoryNames = Object.keys(catalog || {});
  const [activeCat, setActiveCat] = useState(categoryNames[0] || "");
  const [justAdded, setJustAdded] = useState(null);
  useEffect(() => {
    if (categoryNames.length && !activeCat) setActiveCat(categoryNames[0]);
  }, [categoryNames.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (categoryNames.length === 0) {
    return <div className="text-[12px] text-zinc-400 italic px-3 py-3 border border-dashed border-zinc-200 rounded-md text-center">Loading TRP rate card…</div>;
  }
  const items = catalog[activeCat] || [];

  const handleAdd = (it) => {
    onAdd(activeCat, it);
    setJustAdded(it.key);
    setTimeout(() => setJustAdded((curr) => (curr === it.key ? null : curr)), 700);
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="co-upsell-catalog">
      {/* Editorial tabs — thin, monochrome, with active underline */}
      <div className="flex items-center gap-0.5 px-3 pt-2.5 border-b border-zinc-100 overflow-x-auto">
        {categoryNames.map(c => {
          const active = activeCat === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setActiveCat(c)}
              className={`relative px-2.5 py-2 text-[12px] font-medium whitespace-nowrap transition-colors ${active ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`}
              data-testid={`co-upsell-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              {c}
              {active && <span className="absolute left-2.5 right-2.5 -bottom-px h-0.5 bg-zinc-900 rounded-full" />}
            </button>
          );
        })}
      </div>

      {/* Premium service cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-100">
        {items.map(it => {
          const desc = CATALOG_DESCRIPTIONS[it.key];
          const wasAdded = justAdded === it.key;
          return (
            <div key={it.key} className="bg-white px-4 py-3.5 transition-colors hover:bg-zinc-50/60 group flex flex-col" data-testid={`co-upsell-item-${it.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold tracking-tight text-zinc-900 leading-tight">{it.label}</div>
                  {desc && <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{desc}</div>}
                </div>
                <div className="text-right shrink-0">
                  {!isPmRole && it.default_unit_price !== undefined ? (
                    <>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-400 font-medium">Starting at</div>
                      <div className="text-[13.5px] font-semibold tabular-nums text-zinc-900 -mt-0.5">${it.default_unit_price.toLocaleString()}</div>
                    </>
                  ) : (
                    <div className="text-[10px] text-zinc-400 italic">Pricing by Sales</div>
                  )}
                </div>
              </div>

              <div className="mt-2.5 flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-400 font-medium">
                  Per {it.unit_type.replace(/_/g, " ")}
                </div>
                <button
                  type="button"
                  onClick={() => handleAdd(it)}
                  className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11.5px] font-medium border transition-all ${
                    wasAdded
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-zinc-200 text-zinc-700 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white"
                  }`}
                  data-testid={`co-upsell-add-${it.key}`}
                >
                  {wasAdded ? <><CheckCircle2 className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add to Scope</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RevenueScopeEmptyState({ onAdd }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/40 px-6 py-9 text-center" data-testid="co-upsell-empty">
      <div className="w-9 h-9 rounded-lg bg-white border border-zinc-200 mx-auto flex items-center justify-center mb-3">
        <Sparkles className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="text-[13px] font-medium text-zinc-700">No additional deliverables configured yet</div>
      <p className="text-[11.5px] text-zinc-500 mt-1 max-w-xs mx-auto leading-relaxed">
        Add billable production scope from the catalog above or create a custom deliverable.
      </p>
      <Button size="sm" type="button" onClick={onAdd} className="mt-4 h-8 text-[12px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5" data-testid="co-empty-add-custom">
        <Plus className="w-3.5 h-3.5" /> Add Custom Deliverable
      </Button>
    </div>
  );
}

function COScopeItemsEditor({ items, onUpdate, onRemove, itemized = false, showPrice = false, priceOf }) {
  const categoryPicker = (li) => (
    <>
      <Select value={li.co_type || "modeling"} onValueChange={(v) => onUpdate(li.id, { co_type: v, co_type_custom: "" })}>
        <SelectTrigger className="h-8 text-[12.5px] w-40 shrink-0" data-testid={`co-li-type-${li.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {CO_CHANGE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {(li.co_type === "other" || li.co_type_custom) && (
        <Input
          placeholder="Specify…"
          value={li.co_type_custom || ""}
          onChange={(e) => onUpdate(li.id, { co_type_custom: e.target.value })}
          className="h-8 text-[13px] w-36 shrink-0"
          data-testid={`co-li-type-custom-${li.id}`}
        />
      )}
    </>
  );
  const removeButton = (li) => (
    <button type="button" onClick={() => onRemove(li.id)} className="w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0" data-testid={`co-li-remove-${li.id}`}>
      <X className="w-3.5 h-3.5" />
    </button>
  );

  if (!itemized) {
    return (
      <div className="space-y-2" data-testid="co-scope-items">
        {items.map((li, idx) => (
          <div key={li.id} className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-white px-3.5 py-2.5 hover:border-zinc-300 transition-colors">
            <span className="w-5 h-5 rounded-md bg-zinc-100 text-zinc-500 flex items-center justify-center shrink-0 text-[10px] font-bold">{idx + 1}</span>
            <Input
              placeholder="Deliverable name…"
              value={li.item_name}
              onChange={(e) => onUpdate(li.id, { item_name: e.target.value })}
              className="h-8 text-[13px] flex-1 min-w-0"
              data-testid={`co-li-name-${li.id}`}
            />
            {categoryPicker(li)}
            {removeButton(li)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid="co-scope-items">
      {items.map((li, idx) => {
        const declined = li.client_decision === "declined";
        return (
          <div key={li.id} className="rounded-lg border border-zinc-200 bg-white hover:border-zinc-300 transition-colors px-3.5 py-3 space-y-2.5" data-testid={`co-scope-item-${idx}`}>
            <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
              <span className="w-5 h-5 rounded-md bg-zinc-100 text-zinc-500 flex items-center justify-center shrink-0 text-[10px] font-bold">{idx + 1}</span>
              <Input
                placeholder="Item name — e.g. Orbit keyframe updates"
                value={li.item_name}
                onChange={(e) => onUpdate(li.id, { item_name: e.target.value })}
                className="h-8 text-[13px] font-medium flex-1 min-w-[160px]"
                data-testid={`co-li-name-${li.id}`}
              />
              {categoryPicker(li)}
              {removeButton(li)}
            </div>
            {declined && (
              <div className="ml-7 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                The client declined this item — it is not billed or produced unless they approve it again.
              </div>
            )}
            <div className="pl-7 space-y-2.5">
              <Textarea
                rows={2}
                placeholder="What the work involves — the client reads this under the item name."
                value={li.description || ""}
                onChange={(e) => onUpdate(li.id, { description: e.target.value })}
                className="text-[12.5px] leading-[1.55] resize-none border-zinc-200"
                data-testid={`co-li-desc-${li.id}`}
              />
              <div className="flex items-end gap-2.5 flex-wrap">
                <div className="space-y-1 w-20">
                  <FieldLabel small>Quantity</FieldLabel>
                  <Input type="number" min="0" step="1" value={li.quantity} onChange={(e) => onUpdate(li.id, { quantity: e.target.value })} className="h-8 text-[12.5px]" data-testid={`co-li-qty-${li.id}`} />
                </div>
                <div className="space-y-1 w-32">
                  <FieldLabel small>Deliverable</FieldLabel>
                  <Select value={li.unit_type || "feature"} onValueChange={(v) => onUpdate(li.id, { unit_type: v })}>
                    <SelectTrigger className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNIT_TYPES.map(u => <SelectItem key={u} value={u}>{u.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {showPrice && (
                  <div className="space-y-1 ml-auto w-36">
                    <FieldLabel small>Price</FieldLabel>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11.5px] text-zinc-400">$</span>
                      <Input
                        type="number" min="0" step="50"
                        value={li.price_override ?? ""}
                        onChange={(e) => onUpdate(li.id, { price_override: e.target.value })}
                        placeholder={priceOf && priceOf(li) > 0 ? String(priceOf(li)) : "From estimate"}
                        className="h-8 text-[12.5px] pl-5 tabular-nums"
                        data-testid={`co-li-price-${li.id}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// One item's production estimate on an itemized change order (Step 3): how
// it gets made, by whom, for how many hours or at what studio cost — and the
// price that falls out of it.
function ItemProductionCard({ item, index, isPmRole, recommended, ready, multiplierFor, onChange }) {
  const method = item.production_method || "in_house";
  const efforts = item.item_efforts || [];
  const vendorsList = item.item_vendors || [];
  const usesEfforts = method === "in_house" || method === "hybrid";
  const usesVendors = method === "outsourced" || method === "hybrid";
  const setEffort = (i, patch) => onChange({ item_efforts: efforts.map((e, j) => j === i ? { ...e, ...patch } : e) });
  const setVendor = (i, patch) => onChange({ item_vendors: vendorsList.map((v, j) => j === i ? { ...v, ...patch } : v) });
  const vendorCost = vendorsList.reduce((s, v) => s + (parseFloat(v.cost) || 0), 0);
  const overrideOn = String(item.price_override ?? "").trim() !== "";
  const categoryLabel = item.co_type === "other"
    ? (item.co_type_custom || "Other")
    : (CO_CHANGE_TYPES.find(t => t.value === item.co_type)?.label || item.category);

  return (
    <div className={`rounded-lg border bg-white overflow-hidden ${item.client_decision === "declined" ? "border-zinc-200 opacity-60" : "border-zinc-200"}`} data-testid={`co-item-production-${index}`}>
      <div className="flex items-start justify-between gap-3 px-3.5 pt-3 pb-2.5 border-b border-zinc-100">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-zinc-900 truncate">
            <span className="text-zinc-400 mr-1.5 tabular-nums">{index + 1}.</span>{item.item_name || "Untitled item"}
          </div>
          <div className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-medium mt-0.5">
            {categoryLabel}{item.client_decision === "declined" ? " · declined by client" : ""}
          </div>
        </div>
        <div className="flex rounded-md border border-zinc-200 p-0.5 shrink-0" role="radiogroup" aria-label="Production method">
          {PRODUCTION_METHODS.map(m => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={method === m.value}
              onClick={() => onChange({ production_method: m.value })}
              className={`px-2.5 py-1 rounded text-[11.5px] font-medium transition-colors ${method === m.value ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
              data-testid={`co-item-method-${index}-${m.value}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-3.5 py-3 space-y-3">
        {usesEfforts && (
          <div className="space-y-1.5">
            <FieldLabel small>Artists &amp; hours</FieldLabel>
            {efforts.map((e, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="number" min="0" step="0.25" placeholder="Hours"
                  value={e.estimatedHours}
                  onChange={(ev) => setEffort(i, { estimatedHours: ev.target.value })}
                  className="h-8 text-[12.5px] w-24"
                  data-testid={`co-item-hours-${index}-${i}`}
                />
                <Select value={e.artistType} onValueChange={(v) => setEffort(i, { artistType: v })}>
                  <SelectTrigger className="h-8 text-[12.5px] flex-1 min-w-0" data-testid={`co-item-artist-${index}-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ARTIST_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {efforts.length > 1 && (
                  <button type="button" onClick={() => onChange({ item_efforts: efforts.filter((_, j) => j !== i) })} className="w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => onChange({ item_efforts: [...efforts, emptyEffort()] })} className="text-[11.5px] text-zinc-500 hover:text-zinc-900">
              + Add another artist
            </button>
          </div>
        )}

        {usesVendors && (
          <div className="space-y-1.5">
            <FieldLabel small>Outsource studio</FieldLabel>
            {vendorsList.map((v, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                <Input
                  placeholder="Studio name"
                  value={v.name}
                  onChange={(ev) => setVendor(i, { name: ev.target.value })}
                  className="h-8 text-[12.5px] flex-1 min-w-[120px]"
                  data-testid={`co-item-vendor-${index}-${i}`}
                />
                {isPmRole ? (
                  <div className="h-8 px-2 flex items-center text-[11px] text-zinc-400 italic bg-zinc-50 rounded border border-zinc-200 w-28 shrink-0">Cost by Sales</div>
                ) : (
                  <div className="relative w-28 shrink-0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11.5px] text-zinc-400">$</span>
                    <Input
                      type="number" min="0" step="100" placeholder="Cost"
                      value={v.cost}
                      onChange={(ev) => setVendor(i, { cost: ev.target.value })}
                      className="h-8 text-[12.5px] pl-5 tabular-nums"
                      data-testid={`co-item-vendor-cost-${index}-${i}`}
                    />
                  </div>
                )}
                <Input
                  placeholder="Timeline"
                  value={v.timeline}
                  onChange={(ev) => setVendor(i, { timeline: ev.target.value })}
                  className="h-8 text-[12.5px] w-28 shrink-0"
                />
                {vendorsList.length > 1 && (
                  <button type="button" onClick={() => onChange({ item_vendors: vendorsList.filter((_, j) => j !== i) })} className="w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => onChange({ item_vendors: [...vendorsList, emptyVendor()] })} className="text-[11.5px] text-zinc-500 hover:text-zinc-900">
                + Add another studio
              </button>
              {!isPmRole && vendorCost > 0 && (
                <span className="text-[11px] text-zinc-400 tabular-nums">{multiplierFor(vendorCost)}× on {formatCurrency(vendorCost)} (auto-tiered)</span>
              )}
            </div>
          </div>
        )}

        {!ready && (
          <div className="text-[11px] text-zinc-500">
            {isPmRole ? "Add the hours or the studio to price this item." : "Add hours or a studio cost — or type a price — to price this item."}
          </div>
        )}
      </div>

      {!isPmRole && (
        <div className="px-3.5 py-2 border-t border-zinc-100 bg-zinc-50 flex items-center justify-between gap-3">
          <span className="text-[11px] text-zinc-500 tabular-nums">
            Recommended {formatCurrency(recommended)}
            {overrideOn && (
              <button type="button" onClick={() => onChange({ price_override: "" })} className="ml-2 underline underline-offset-2 hover:text-zinc-900" data-testid={`co-item-use-recommended-${index}`}>
                Use recommended
              </button>
            )}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-medium">Price</span>
            <div className="relative w-32">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11.5px] text-zinc-400">$</span>
              <Input
                type="number" min="0" step="50"
                value={item.price_override ?? ""}
                onChange={(e) => onChange({ price_override: e.target.value })}
                placeholder={String(recommended || 0)}
                className="h-8 text-[13px] pl-5 tabular-nums font-semibold"
                data-testid={`co-item-price-${index}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function describeItemProduction(li) {
  const parts = [];
  if (li.production_method === "in_house" || li.production_method === "hybrid") {
    for (const e of li.item_efforts || []) {
      if (!parseFloat(e.estimatedHours)) continue;
      parts.push(`${parseFloat(e.estimatedHours)}h ${ARTIST_TYPES.find(a => a.value === e.artistType)?.label || e.artistType}`);
    }
  }
  if (li.production_method === "outsourced" || li.production_method === "hybrid") {
    for (const v of li.item_vendors || []) if (v.name) parts.push(v.name);
  }
  return parts.join(" · ");
}

function ItemizedReviewTable({ items, isPmRole, priceOf }) {
  return (
    <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100" data-testid="co-review-items">
      {items.map((li, idx) => {
        const declined = li.client_decision === "declined";
        const categoryLabel = li.co_type === "other"
          ? (li.co_type_custom || "Other")
          : (CO_CHANGE_TYPES.find(t => t.value === li.co_type)?.label || li.category);
        return (
          <div key={li.id} className="flex items-start justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <div className={`text-[13px] font-medium ${declined ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
                {idx + 1}. {li.item_name}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {[categoryLabel, describeItemProduction(li), declined ? "declined by client" : null].filter(Boolean).join(" · ")}
              </div>
            </div>
            {!isPmRole && (
              <div className={`text-[13px] font-semibold tabular-nums shrink-0 ${declined ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
                {formatCurrency(priceOf(li))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LineItemsEditor({ items, isPmRole, onUpdate, onRemove, projectCommitments }) {
  if (items.length === 0) {
    return null; // Empty state handled by parent (premium ScopeItemEmptyState)
  }
  return (
    <div className="space-y-2" data-testid="co-line-items">
      {items.map(li => (
        <div key={li.id} className="rounded-lg border border-zinc-200 bg-white hover:border-zinc-300 transition-colors overflow-hidden group">
          {/* Card header — collapsed-style identity row */}
          <div className="flex items-start justify-between gap-3 px-3.5 pt-3 pb-2 border-b border-zinc-100">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-md bg-zinc-100 text-zinc-500 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-zinc-900 group-hover:text-white transition-colors">
                <Layers className="w-3 h-3" />
              </div>
              <div className="min-w-0 flex-1">
                <Input
                  placeholder="Line item title (e.g. Additional Bedroom Render)"
                  value={li.item_name}
                  onChange={(e) => onUpdate(li.id, { item_name: e.target.value })}
                  className="h-7 text-[13px] font-medium px-0 border-0 shadow-none focus-visible:ring-0 placeholder:text-zinc-400"
                  data-testid={`co-li-name-${li.id}`}
                />
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {li.category && (
                    <span className="text-[9.5px] uppercase tracking-wider text-zinc-400 font-medium">{li.category}</span>
                  )}
                  {li.linked_commitment_id && (
                    <>
                      <span className="text-zinc-300">•</span>
                      <span className="text-[10.5px] text-zinc-500 inline-flex items-center gap-1">
                        <Link2 className="w-2.5 h-2.5" />
                        Linked to original commitment
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button type="button" onClick={() => onRemove(li.id)} className="w-6 h-6 rounded-md flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0" data-testid={`co-li-remove-${li.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Card body — operational metadata grid */}
          <div className="px-3.5 py-3 grid grid-cols-12 gap-2.5">
            <div className="col-span-3 space-y-1">
              <FieldLabel small>Quantity</FieldLabel>
              <Input type="number" min="0" step="0.5" value={li.quantity} onChange={(e) => onUpdate(li.id, { quantity: e.target.value })} className="h-8 text-[12.5px]" data-testid={`co-li-qty-${li.id}`} />
            </div>
            <div className="col-span-3 space-y-1">
              <FieldLabel small>Unit</FieldLabel>
              <Select value={li.unit_type || "feature"} onValueChange={(v) => onUpdate(li.id, { unit_type: v })}>
                <SelectTrigger className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3 space-y-1">
              <FieldLabel small>Related Space</FieldLabel>
              <Input placeholder="e.g. Lobby" value={li.related_space || ""} onChange={(e) => onUpdate(li.id, { related_space: e.target.value })} className="h-8 text-[12.5px]" />
            </div>
            <div className="col-span-3 space-y-1">
              <FieldLabel small>{isPmRole ? "Pricing" : "Unit Price"}</FieldLabel>
              {isPmRole ? (
                <div className="h-8 px-2 flex items-center text-[11px] text-zinc-400 italic bg-zinc-50/50 rounded border border-zinc-200">By Sales</div>
              ) : (
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11.5px] text-zinc-400">$</span>
                  <Input type="number" min="0" step="50" value={li.unit_price} onChange={(e) => onUpdate(li.id, { unit_price: e.target.value })} className="h-8 text-[12.5px] pl-5 tabular-nums" data-testid={`co-li-price-${li.id}`} />
                </div>
              )}
            </div>

            <div className="col-span-12 space-y-1">
              <FieldLabel small>Description</FieldLabel>
              <Input placeholder="Notes for this deliverable (optional)…" value={li.description || ""} onChange={(e) => onUpdate(li.id, { description: e.target.value })} className="h-8 text-[12.5px]" />
            </div>

            {projectCommitments && projectCommitments.length > 0 && (
              <div className="col-span-12 space-y-1">
                <FieldLabel small>Linked Original Commitment <span className="text-zinc-400 font-normal normal-case">(optional)</span></FieldLabel>
                <Select value={li.linked_commitment_id || "__none__"} onValueChange={(v) => onUpdate(li.id, { linked_commitment_id: v === "__none__" ? null : v })}>
                  <SelectTrigger className="h-8 text-[12.5px]"><SelectValue placeholder="Link to original commitment…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-zinc-400">— None —</span></SelectItem>
                    {projectCommitments.filter(c => c.source !== "additional_scope").map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.deliverable_type} ×{c.quantity || 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Card footer — running total */}
          {!isPmRole && (
            <div className="px-3.5 py-2 border-t border-zinc-100 bg-zinc-50/40 flex items-center justify-between">
              <span className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-medium">Line Total</span>
              <span className="text-[13px] font-semibold tabular-nums text-zinc-900">
                ${Number(li.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Step 2 architectural primitives ────────────────────────────────────────
function Section({ title, caption, action, muted, children }) {
  return (
    <section className={muted ? "" : ""}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <h3 className={`text-[14px] font-semibold tracking-tight leading-tight ${muted ? "text-zinc-700" : "text-zinc-900"}`}>{title}</h3>
          {caption && <p className="text-[12px] text-zinc-500 mt-0.5 leading-relaxed">{caption}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ children, small }) {
  return (
    <label className={`block font-medium text-zinc-600 ${small ? "text-[10px] uppercase tracking-[0.08em]" : "text-[11px]"}`}>{children}</label>
  );
}

function ScopeItemEmptyState({ onAdd }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/40 px-6 py-9 text-center" data-testid="co-line-items-empty">
      <div className="w-9 h-9 rounded-lg bg-white border border-zinc-200 mx-auto flex items-center justify-center mb-3">
        <Layers className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="text-[13px] font-medium text-zinc-700">No structured scope items added yet</div>
      <p className="text-[11.5px] text-zinc-500 mt-1 max-w-xs mx-auto leading-relaxed">
        Add production-linked deliverables, revisions, or additional commitments to define this request.
      </p>
      <Button size="sm" type="button" onClick={onAdd} className="mt-4 h-8 text-[12px] bg-zinc-900 hover:bg-zinc-800 text-white gap-1.5" data-testid="co-empty-add-line-item">
        <Plus className="w-3.5 h-3.5" /> Add Scope Item
      </Button>
    </div>
  );
}

function CommitmentSelector({ commitments, selectedIds, onToggle }) {
  if (commitments.length === 0) {
    return <div className="text-[12px] text-zinc-400 italic px-3 py-3 border border-dashed border-zinc-200 rounded-md text-center">No original commitments on this project.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-0.5" data-testid="co-commitment-selector">
      {commitments.map(c => {
        const selected = selectedIds.includes(c.id);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onToggle(c.id)}
            data-testid={`co-commitment-${c.id}`}
            className={`relative text-left rounded-lg border transition-all duration-150 px-3 py-2.5 group
              ${selected
                ? "border-zinc-900 bg-zinc-50/60 shadow-[0_0_0_3px_rgba(24,24,27,0.04)]"
                : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50/40"}
            `}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium text-zinc-900 truncate">{c.deliverable_type}</div>
                <div className="text-[10.5px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                  <span>×{c.quantity || 1}</span>
                  <span className="text-zinc-300">•</span>
                  <span className="capitalize">{(c.status || "Queued").toLowerCase()}</span>
                </div>
              </div>
              <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 transition-all
                ${selected ? "border-zinc-900 bg-zinc-900" : "border-zinc-300 bg-white"}`}>
                {selected && <CheckCircle2 className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ReferenceUploader({ attachments, onAdd, onRemove }) {
  const isImage = (ct) => (ct || "").startsWith("image/");
  return (
    <div className="space-y-2">
      <label
        className="group flex items-center justify-center gap-2.5 px-4 py-5 border border-dashed border-zinc-300 rounded-lg cursor-pointer hover:border-zinc-400 hover:bg-zinc-50/40 transition-colors"
        data-testid="co-attachments-upload"
      >
        <div className="w-7 h-7 rounded-md bg-zinc-100 group-hover:bg-zinc-900 flex items-center justify-center transition-colors">
          <Upload className="w-3.5 h-3.5 text-zinc-500 group-hover:text-white transition-colors" />
        </div>
        <div className="text-left">
          <div className="text-[12.5px] font-medium text-zinc-700">Drop files here, or <span className="text-zinc-900">click to browse</span></div>
          <div className="text-[10.5px] text-zinc-400">Plans, screenshots, markups, PDFs, client emails</div>
        </div>
        <input type="file" multiple className="hidden" onChange={onAdd} />
      </label>
      {attachments.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {attachments.map((a, idx) => (
            <div key={idx} className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-zinc-200 rounded-md text-[11.5px]">
              <div className="w-6 h-6 rounded bg-zinc-100 flex items-center justify-center shrink-0">
                <Paperclip className="w-3 h-3 text-zinc-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-zinc-900 truncate">{a.filename}</div>
                <div className="text-[9.5px] text-zinc-400">{isImage(a.content_type) ? "Image" : (a.content_type || "File")} · {Math.max(1, Math.round((a.size_bytes || 0) / 1024))} KB</div>
              </div>
              <button type="button" onClick={() => onRemove(idx)} className="w-5 h-5 rounded flex items-center justify-center text-zinc-400 hover:text-rose-600 hover:bg-rose-50 shrink-0">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
